import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { v2Api, type DraftPlan, type DraftSummary, type ProjectDashboard, type TaskDraft, type TaskPlanQualityGate, type V2ModelChoice } from './api';
import type { Project, RecycleBinEntry } from '../types';

function fmt(value?: string | null) {
  return value ? new Intl.DateTimeFormat('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(value)) : '-';
}

function EditableList({ items, onChange, emptyText = '未配置验收标准，需补充后才能人工验收' }: { items: string[]; onChange?: (items: string[]) => void; emptyText?: string }) {
  const cleanItems = (items || []).map((item) => String(item || '').trim()).filter(Boolean);
  const values = cleanItems.length ? cleanItems : [emptyText];
  return (
    <div className="v2-edit-list">
      {values.map((item, index) => (
        <textarea
          key={index}
          rows={2}
          value={item}
          onChange={(e) => {
            const next = [...values];
            next[index] = e.target.value;
            onChange?.(next);
          }}
        />
      ))}
    </div>
  );
}

function humanizeGateAIssue(item: any) {
  const issue = String(item?.issue || item || '大纲内容需要补充');
  const field = String(item?.field_path || '');
  const source = String(item?.source_text || '').trim();
  const example = String(item?.example || '').trim();
  if (/acceptance|验收标准|acceptance_criteria/i.test(`${field} ${issue}`)) {
    return {
      title: '验收标准不够具体',
      location: '阶段验收标准',
      reason: '现在的写法没有说清用户要做什么操作、看到什么结果、系统保存了什么数据，所以无法形成可执行的人工验收标准。',
      source,
      example: example || '例如：打开成品预览，新增一条测试数据，刷新页面后数据仍存在，再删除并确认列表中消失。',
    };
  }
  if (/deliverable|交付物/i.test(`${field} ${issue}`)) {
    return {
      title: '交付物不够明确',
      location: '阶段交付物',
      reason: '现在没有说明最终要交付哪个可打开、可下载或可运行的文件/入口。',
      source,
      example: example || '例如：交付可打开的 Web 预览入口、源码目录、构建说明和下载包。',
    };
  }
  return {
    title: issue.replace(/\s*\([^)]*\)\s*/g, '').trim() || '大纲内容需要补充',
    location: field ? field.replace(/^phases\[\d+\]\./, '阶段内容：') : '项目大纲',
    reason: '这部分内容还不足以支撑后续任务拆解和人工验收。',
    source,
    example: example || '请把目标、产物和通过条件写成用户能操作验证的句子。',
  };
}

function userFacingError(error: unknown) {
  const text = error instanceof Error ? error.message : String(error || '');
  if (/401|auth|api key|LLMAuthError|密钥|unauthorized/i.test(text)) {
    return '模型调用失败：当前模型密钥无效或未通过验证。这个问题不会阻止你手动修改并保存大纲；请到系统设置检查模型配置。';
  }
  if (/task_plan_quality_gate_failed|任务计划质量门|Gate B|B 门|B门/i.test(text)) {
    return '任务计划还不能确认：请先处理页面上列出的任务内容、执行模型或依赖问题。';
  }
  return text || '操作失败，请稍后重试。';
}

function useCapabilityOptions(capability: string) {
  const [models, setModels] = useState<V2ModelChoice[]>([]);
  const [error, setError] = useState('');
  useEffect(() => {
    void v2Api.modelCapabilities()
      .then((rows) => setModels(rows.filter((m) => m.configured && m.capabilities.includes(capability))))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [capability]);
  return { models, error };
}

function isMiniMaxModel(model: V2ModelChoice) {
  const provider = model.provider.toLowerCase();
  return model.configured && provider === 'minimax';
}

function pickMiniMaxModel(models: V2ModelChoice[]) {
  return (
    models.find((m) => isMiniMaxModel(m) && `${m.model} ${m.display_name || ''}`.toLowerCase().includes('minimax-m3')) ||
    models.find(isMiniMaxModel) ||
    null
  );
}

type ModelAssignmentRole = 'planner' | 'decomposer' | 'executor' | 'reviewer' | 'tester';

function modelMatchesAssignment(model: V2ModelChoice, role: Record<string, unknown> | null | undefined) {
  if (!role) return false;
  const provider = String(role.provider || '').toLowerCase();
  const modelName = String(role.model || '').toLowerCase();
  if (!provider || !modelName) return false;
  return model.provider.toLowerCase() === provider && model.model.toLowerCase() === modelName;
}

function pickAssignedModel(models: V2ModelChoice[], assignment: Record<string, unknown> | null, roleName: ModelAssignmentRole) {
  const roles = assignment?.roles as Record<string, Record<string, unknown>> | undefined;
  const role = roles?.[roleName];
  return models.find((model) => model.configured && modelMatchesAssignment(model, role)) || null;
}

function pickPreferredModel(models: V2ModelChoice[], assignment: Record<string, unknown> | null, roleName: ModelAssignmentRole) {
  return pickAssignedModel(models, assignment, roleName)
    || models.find((model) => model.configured && model.is_default)
    || (roleName === 'planner' ? pickMiniMaxModel(models) : null)
    || models.find((model) => model.configured)
    || null;
}

function modelChoiceText(model: V2ModelChoice | null | undefined) {
  return model ? `${model.display_name || model.model}（${model.provider}）` : '';
}

function compactModels(models: Array<V2ModelChoice | null | undefined>) {
  const seen = new Set<string>();
  return models.filter((model): model is V2ModelChoice => {
    if (!model || seen.has(model.id)) return false;
    seen.add(model.id);
    return true;
  });
}

function isExecutionReadyModel(model: V2ModelChoice) {
  return model.configured
    && model.execution_adapter_status === 'connected'
    && !!model.execution_adapter
    && model.capabilities.includes('real_file_write');
}

function assignmentLabel(assignment: Record<string, unknown> | null, roleName: ModelAssignmentRole) {
  const roles = assignment?.roles as Record<string, Record<string, unknown>> | undefined;
  const role = roles?.[roleName];
  return role?.provider && role?.model ? `${role.provider}/${role.model}` : '';
}

function useModelAssignment() {
  const [assignment, setAssignment] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    void v2Api.modelAssignment().then(setAssignment).catch(() => setAssignment(null));
  }, []);
  return assignment;
}

function hasAcceptance(items: unknown) {
  return Array.isArray(items) && items.some((item) => String(item || '').trim());
}

function isUuidLike(value?: string | null) {
  return !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function executionModelValue(model: V2ModelChoice) {
  if (model.id === 'codex-cli') return 'codex-cli';
  return `${model.provider}:${model.model}`;
}

function findExecutionModelByValue(value: string | null | undefined, models: V2ModelChoice[]) {
  if (!value) return null;
  const current = value.toLowerCase();
  const matched = models.find((model) => [
    executionModelValue(model),
    model.id,
  ].filter(Boolean).some((candidate) => String(candidate).toLowerCase() === current));
  if (matched) return matched;
  const aliasMatches = models.filter((model) => [
    model.model,
    model.display_name,
  ].filter(Boolean).some((candidate) => String(candidate).toLowerCase() === current));
  return aliasMatches.length === 1 ? aliasMatches[0] : null;
}

function uniqueExecutionAdapters(models: V2ModelChoice[]) {
  return Array.from(new Set(models.map((m) => m.execution_adapter).filter(Boolean) as string[]));
}

function useDraftPlan(sessionId?: string) {
  const [plan, setPlan] = useState<DraftPlan | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError('');
    try {
      setPlan(await v2Api.getDraftPlan(sessionId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [sessionId]);
  useEffect(() => { void load(); }, [load]);
  return { plan, setPlan, loading, error, reload: load };
}

function selectedModelText(models: V2ModelChoice[], id: string, fallback?: string) {
  const model = models.find((m) => m.id === id);
  if (!model) return fallback && fallback !== '未指定' ? fallback : '尚未选择';
  return `${model.display_name || model.model}（提供方：${model.provider}）`;
}

const VAGUE_PLANNING_TERMS = ['基础实现', '主流程联通', '验证与验收准备', '核心交付物', '核心主流程', '关键数据', '主要结果', '达到阶段验收要求', '验收材料', '相关文档', '说明文档'];
const OBSERVABLE_ACCEPTANCE_TERMS = ['新增', '编辑', '删除', '保存', '刷新', '打开', '点击', '选择', '输入', '进入', '提交', '生成', '播放', '切换', '标记', '下载', '显示', '展示', '查看', '确认', '记录', '读取', '返回', '搜索', '筛选', '跳转', '可见', '评分', '反馈', '更新', '完成', '完整', '通过', '走通', '不提交', '持久化', '正确率'];

function vaguePlanningText(value: unknown) {
  const text = String(value || '').trim();
  return !text || VAGUE_PLANNING_TERMS.some((term) => text.includes(term));
}

function observableAcceptance(value: unknown) {
  const text = String(value || '').trim();
  return text.length >= 5 && !vaguePlanningText(text) && OBSERVABLE_ACCEPTANCE_TERMS.some((term) => text.includes(term));
}

function outlineQuality(plan: DraftPlan) {
  if (plan.outline_quality_gate) return plan.outline_quality_gate;
  const reasons: string[] = [];
  if (!plan.stages.length) reasons.push('缺少项目阶段');
  plan.stages.forEach((stage, index) => {
    const name = `阶段 ${index + 1}`;
    if (!stage.name || /^阶段\s*\d+/.test(stage.name)) reasons.push(`${name} 缺少明确阶段名称`);
    if (!stage.goal) reasons.push(`${name} 缺少阶段目标`);
    if (!stage.module_scope) reasons.push(`${name} 缺少阶段模块范围`);
    if (!stage.deliverables.length) reasons.push(`${name} 缺少阶段交付物`);
    else if (stage.deliverables.some(vaguePlanningText)) reasons.push(`${name} 阶段交付物过于空泛`);
    if (!stage.risks.length) reasons.push(`${name} 缺少阶段风险`);
    if (!stage.stage_acceptance.length) reasons.push(`${name} 缺少阶段验收目标`);
    else if (stage.stage_acceptance.some((item) => !observableAcceptance(item))) reasons.push(`${name} 阶段验收目标缺少可操作检查点`);
  });
  const generic = ['核心主流程可走通', '关键数据可保存', '用户可查看主要结果'];
  if (plan.project_acceptance.length < 4 || plan.project_acceptance.every((item) => generic.includes(item))) {
    reasons.push('项目整体验收目标仍过于通用');
  } else if (plan.project_acceptance.some((item) => !observableAcceptance(item))) {
    reasons.push('项目整体验收目标缺少可操作检查点');
  }
  return { passed: reasons.length === 0, reasons };
}

// Phase 2 main chain helpers — translate between the persistent
// ``GET /blueprint`` response shape and the local draft-outline form
// used by the page, and back to the ``POST /outline`` payload.
function normalizeBlueprintToOutline(bp: any): any {
  const ctx = (bp.context_requirements ?? {}) as Record<string, any>;
  const canonicalScope = scopeText(bp.project_scope ?? ctx.project_scope ?? '');
  return {
    name: bp.name ?? '',
    positioning: bp.positioning ?? '',
    target_users: bp.target_users ?? '',
    business_context: bp.business_context ?? '',
    overall_goal: bp.project_goal ?? ctx.project_goal ?? '',
    project_scope: canonicalScope,
    scope: canonicalScope ? [canonicalScope] : (bp.core_features ?? []),
    out_of_scope: bp.out_of_scope ?? [],
    risks: parseRisksField(bp.risk_points),
    project_acceptance: bp.project_acceptance_criteria ?? ctx.project_acceptance_criteria ?? [],
    // Server stores phase_goal/module_scope/deliverables/risks inside
    // ``task_package_source`` (nested).  The previous read path looked at
    // ``ph.phase_goal`` etc. only — which always came back as ``undefined``
    // — so every save wiped the LLM-generated data, Gate A failed with
    // ``阶段 X 缺少预期交付物`` and the user could never reach task-plan.
    // Read nested first, fall back to top-level for legacy rows.
    phases: (bp.phases ?? []).map((ph: any) => {
      const tps = (ph.task_package_source ?? {}) as Record<string, any>;
      return {
        id: ph.id,
        phase_number: ph.phase_number,
        name: ph.name,
        task_scope: ph.task_scope ?? '',
        phase_goal: tps.phase_goal ?? ph.phase_goal ?? '',
        module_scope: tps.module_scope ?? ph.module_scope ?? '',
        deliverables: parseMaybeJsonArray(tps.deliverables ?? ph.deliverables),
        risks: parseRisksField(tps.risks ?? ph.risks),
        stage_acceptance: (ph.acceptance_criteria ?? []).map((c: any) => c.criterion),
      };
    }),
  };
}

/** ``project_scope`` is a single business-context string on every wire. */
function scopeText(value: any): string {
  if (Array.isArray(value)) {
    return value.map((item) => String(item ?? '').trim()).filter(Boolean).join('；');
  }
  return String(value ?? '').trim();
}

/** True iff the freshly-loaded blueprint has any risk items whose
 * ``confirmation_required === true`` (these would block confirm). */
function hasOutstandingRiskConfirmations(bp: any): boolean {
  if (!bp) return false;
  const risks = Array.isArray(bp.risk_points) ? bp.risk_points : [];
  for (const raw of risks) {
    if (!raw || typeof raw !== 'object') continue;
    if ((raw as { confirmation_required?: boolean }).confirmation_required === true) {
      return true;
    }
  }
  return false;
}


function outlineToPayload(outline: any): any {
  return {
    name: outline.name ?? '',
    positioning: outline.positioning ?? '',
    target_users: outline.target_users ?? '',
    business_context: outline.business_context ?? '',
    // Send ``task_package_source`` as the canonical nested object so the
    // backend's ``_attach_blueprint_from_dict`` round-trip preserves phase
    // deliverables / risks even when top-level fields are also empty.
    phases: (outline.phases ?? []).map((ph: any, idx: number) => ({
      phase_number: ph.phase_number ?? idx + 1,
      name: ph.name ?? '',
      task_scope: ph.task_scope ?? '',
      phase_goal: ph.phase_goal ?? '',
      module_scope: ph.module_scope ?? '',
      deliverables: ph.deliverables ?? [],
      risks: ph.risks ?? [],
      task_package_source: {
        phase_goal: ph.phase_goal ?? '',
        module_scope: ph.module_scope ?? '',
        deliverables: ph.deliverables ?? [],
        risks: ph.risks ?? [],
        source: 'frontend_save',
      },
      criteria: (ph.stage_acceptance ?? []).map((c: any) => ({
        criterion: typeof c === 'string' ? c : (c?.criterion ?? ''),
        priority: 'must',
      })),
    })),
    out_of_scope: outline.out_of_scope ?? [],
    risk_points: outline.risks ?? [],
    project_goal: outline.overall_goal ?? '',
    project_scope: scopeText(outline.project_scope) || scopeText(outline.scope),
    project_acceptance_criteria: outline.project_acceptance ?? [],
  };
}

function humanizeGateBIssue(issue: any) {
  const category = String(issue?.category || '');
  const text = String(issue?.text || issue?.message || issue || '').replace(/\s*\(id=[^)]+\)/g, '').trim();
  const detailContext = {
    problemType: String(issue?.problem_type || issue?.code || category || '任务计划质量问题'),
    requirement: String(issue?.requirement_text || issue?.source_clause || '').trim(),
    sourceRequirement: String(issue?.source_requirement || '').trim(),
    task: String(issue?.task_title || '').trim(),
    relatedTasks: Array.isArray(issue?.related_task_titles) ? issue.related_task_titles.filter(Boolean).join('、') : '',
    missingField: String(issue?.missing_field || issue?.field_path || '').trim(),
    current: issue?.current_value == null || issue?.current_value === '' ? '' : String(
      typeof issue.current_value === 'object' ? JSON.stringify(issue.current_value, null, 2) : issue.current_value,
    ),
    suggestion: String(issue?.suggested_fix || '').trim(),
  };
  if (category === 'fields') {
    return {
      ...detailContext,
      title: '任务内容还不够具体',
      detail: text.includes('验收标准') ? '任务验收标准没有写清楚可操作检查步骤。' : (text || '任务目标、交付物或验收标准需要补清楚。'),
      action: '系统可以自动补成“打开入口、完成操作、刷新后仍存在”的检查标准。',
    };
  }
  if (category === 'whitelist') {
    return {
      ...detailContext,
      title: '执行模型或执行代理不匹配',
      detail: text || '任务没有绑定可用执行模型或执行代理。',
      action: '请选择一个已接入真实执行的模型，并应用到全部任务。',
    };
  }
  if (category === 'coverage') {
    return {
      ...detailContext,
      title: '有需求没有落到任务里',
      detail: text || '原始需求中的某个核心目标没有对应任务。',
      action: '需要补充对应任务，不能直接确认计划。',
    };
  }
  if (category === 'graph') {
    return {
      ...detailContext,
      title: '任务依赖关系有问题',
      detail: text || '任务依赖不存在、跨阶段或形成循环。',
      action: '需要调整任务顺序或依赖关系。',
    };
  }
  if (category === 'scope') {
    return {
      ...detailContext,
      title: '任务超出了项目范围',
      detail: text || '任务包含了大纲中明确不做的内容。',
      action: '删除或改写超出范围的任务。',
    };
  }
  return {
    ...detailContext,
    title: '任务计划需要修改',
    detail: text || '任务草稿还没有达到确认条件。',
    action: '请根据提示修改后重新检查。',
  };
}

function taskPlanGatePassed(gateB: any) {
  const issues = Array.isArray(gateB?.issues) ? gateB.issues : [];
  const categories = gateB?.categories ?? {};
  const categoryTotal = Object.values(categories).reduce((sum: number, value: any) => sum + Number(value || 0), 0);
  return !!gateB?.passed && issues.length === 0 && categoryTotal === 0;
}

/** Safely parse a JSON-stringified array; return as-is if already an array. */
function parseMaybeJsonArray(value: unknown): any[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim().startsWith('[')) {
    try { return JSON.parse(value); } catch { /* fall through */ }
  }
  return value != null ? [value] : [];
}

/**
 * Parse a "risks" field that may contain JSON strings, semicolon-delimited
 * JSON objects, or plain strings — always returns an array of items.
 */
function parseRisksField(value: unknown): any[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    const out: any[] = [];
    for (const item of value) {
      if (typeof item === 'string' && item.trim().startsWith('{')) {
        out.push(...parseRisksField(item));
      } else {
        out.push(item);
      }
    }
    return out;
  }
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return [];
    // JSON array
    if (s.startsWith('[')) {
      try { const p = JSON.parse(s); return Array.isArray(p) ? p : [p]; } catch { /* fall through */ }
    }
    // Single JSON object
    if (s.startsWith('{') && s.endsWith('}')) {
      try { return [JSON.parse(s)]; } catch { /* fall through */ }
    }
    // Multiple JSON objects joined by Chinese/English semicolons
    if (s.includes('{')) {
      const parts = s.split(/[;；]/).map(x => x.trim()).filter(Boolean);
      const out: any[] = [];
      let ok = false;
      for (const part of parts) {
        if (part.startsWith('{')) {
          try { out.push(JSON.parse(part)); ok = true; continue; } catch { /* not JSON */ }
        }
        out.push(part);
      }
      return ok ? out : [s];
    }
    return [s];
  }
  return [value];
}

/**
 * Safely render a risk item (string, JSON-string, or structured object)
 * to human-readable text. Never returns raw JSON or [object Object].
 */
function normalizeRiskItem(item: unknown): string {
  if (typeof item === 'string') {
    const s = item.trim();
    if (s.startsWith('{') || s.startsWith('[')) {
      try {
        const parsed = JSON.parse(s);
        return normalizeRiskItem(parsed);
      } catch { return s; }
    }
    return s;
  }
  if (item && typeof item === 'object' && !Array.isArray(item)) {
    const obj = item as Record<string, unknown>;
    const parts: string[] = [];
    if (typeof obj.description === 'string') parts.push(obj.description);
    if (typeof obj.risk === 'string' && !parts.includes(obj.risk)) parts.push(obj.risk);
    if (typeof obj.flag === 'string') parts.push(`[${obj.flag}]`);
    if (parts.length) return parts.join(' — ');
    if (typeof obj.title === 'string') return obj.title;
    if (typeof obj.name === 'string') return obj.name;
    if (typeof obj.content === 'string') return obj.content;
    return Object.values(obj).filter(v => typeof v === 'string' && v.trim()).map(v => (v as string).trim()).join('；') || '风险项';
  }
  if (Array.isArray(item)) return item.map(normalizeRiskItem).filter(Boolean).join('；');
  return String(item ?? '');
}

export function HomeV2() {
  const navigate = useNavigate();
  const [requirement, setRequirement] = useState('');
  const [drafts, setDrafts] = useState<DraftSummary[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [picker, setPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const { models, error: modelError } = useCapabilityOptions('requirement_understanding');
  const modelAssignment = useModelAssignment();
  const [clarifyModelKey, setClarifyModelKey] = useState('');
  const [assignmentHint, setAssignmentHint] = useState('');
  const [existingOutline, setExistingOutline] = useState('');
  useEffect(() => {
    if (!models.length) return;
    const selected = pickPreferredModel(models, modelAssignment, 'planner');
    setClarifyModelKey((v) => v || selected?.id || '');
    setAssignmentHint(selected
      ? `将使用系统设置中已配置的 ${modelChoiceText(selected)} 进行需求理解；正式链路不会静默回退到未配置模型。`
      : '未找到已配置的需求理解模型，无法启动正式主链。');
  }, [models, modelAssignment]);
  useEffect(() => {
    void v2Api.listDrafts().then(setDrafts).catch(() => setDrafts([]));
    void v2Api.listProjects('active').then(setProjects).catch(() => setProjects([]));
  }, []);
  const selectedClarify = models.find((m) => m.id === clarifyModelKey && m.configured);
  const start = async () => {
    if (!requirement.trim()) return;
    const activeClarify = selectedClarify || pickPreferredModel(models, modelAssignment, 'planner');
    if (!activeClarify?.id || !activeClarify.configured) {
      setMessage('未找到已配置的需求理解模型。请先在系统设置中配置并测试通过模型。');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      const session = await v2Api.createDraft(
        requirement.trim(),
        null,
        activeClarify.id,
        false,
      );
      navigate(`/draft/${session.id}/clarify`);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const startFromExistingOutline = async () => {
    const text = existingOutline.trim();
    if (!text) {
      setMessage('请先粘贴已有项目大纲。');
      return;
    }
    const activeClarify = selectedClarify || pickPreferredModel(models, modelAssignment, 'planner');
    if (!activeClarify?.id || !activeClarify.configured) {
      setMessage('未找到已配置的规划模型。请先在系统设置中配置并测试通过模型。');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      const raw = [
        '已有项目大纲直接导入。',
        '用户已经提供项目大纲，不需要先进行需求澄清。',
        '请直接进入项目大纲检查、任务拆解和后续主链。',
        '已有项目大纲正文:',
        text,
      ].join('\n');
      const session = await v2Api.createDraft(raw, activeClarify.id, activeClarify.id, false);
      navigate(`/draft/${session.id}/outline`);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="v2-page">
      <header className="v2-hero">
        <div>
          <span>项目入口</span>
          <h1>把需求变成可验收的项目计划</h1>
          <p>从需求澄清开始，先确认项目大纲，再确认任务拆解、验收标准和执行路线。</p>
        </div>
      </header>
      <div className="v2-start-grid">
        <section className="v2-panel">
          <h2>新建草稿</h2>
          <textarea rows={6} value={requirement} onChange={(e) => setRequirement(e.target.value)} placeholder="输入一个真实项目需求" />
          {modelError ? <p className="error-banner">模型列表读取失败：{modelError}</p> : null}
          {assignmentHint ? <p className="v2-help">{assignmentHint}</p> : null}
          {message ? <p className="error-banner">{message}</p> : null}
          <button className="primary-button" disabled={busy || !requirement.trim()} onClick={() => void start()}>开始需求澄清</button>
        </section>
        <section className="v2-panel">
          <h2>继续草稿</h2>
          {drafts.slice(0, 4).map((draft) => (
            <Link className="v2-row-link" key={draft.session_id} to={`/draft/${draft.session_id}/clarify`}>
              <strong>{draft.name}</strong><span>{draft.current_step} · {fmt(draft.updated_at)}</span>
            </Link>
          ))}
          <Link className="ghost-button" to="/drafts">查看全部草稿</Link>
        </section>
        <section className="v2-panel">
          <h2>接手项目</h2>
          <Link className="ghost-button" to="/project-takeover?source=local">接手本地项目</Link>
          <Link className="ghost-button" to="/project-takeover?source=github">接手 GitHub 项目</Link>
          <button className="ghost-button" onClick={() => setPicker(true)}>选择已有项目</button>
        </section>
        <section className="v2-panel">
          <h2>已有大纲</h2>
          <p className="v2-help">如果你已经有项目大纲，粘贴后直接进入大纲检查和任务拆解，不先做需求澄清。</p>
          <textarea
            rows={6}
            value={existingOutline}
            onChange={(e) => setExistingOutline(e.target.value)}
            placeholder="粘贴已有项目大纲、阶段、交付物和验收标准"
            data-testid="v2-existing-outline-input"
          />
          <button
            className="primary-button"
            disabled={busy || !existingOutline.trim()}
            onClick={() => void startFromExistingOutline()}
            data-testid="v2-existing-outline-start"
          >进入大纲检查</button>
        </section>
        <section className="v2-panel">
          <h2>快速演示</h2>
          <Link className="ghost-button" to="/live-demo">打开公开演示</Link>
          <p className="v2-help">当前为快速演示，不创建正式项目，不执行真实代码。</p>
        </section>
      </div>
      {picker ? (
        <div className="v2-modal" role="dialog">
          <div className="v2-modal-card">
            <header><h2>选择已有项目</h2><button className="ghost-button" onClick={() => setPicker(false)}>关闭</button></header>
            {projects.map((project) => (
              <button className="v2-row-button" key={project.id} onClick={() => navigate(`/project/${project.id}`)}>
                <strong>{project.name}</strong><span>{project.slug}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}

export function DraftsV2() {
  const [drafts, setDrafts] = useState<DraftSummary[]>([]);
  const load = () => v2Api.listDrafts().then(setDrafts);
  useEffect(() => { void load(); }, []);
  const remove = async (id: string) => {
    await v2Api.deleteDraft(id);
    await load();
  };
  return (
    <section className="v2-page">
      <header className="v2-title"><h1>草稿计划</h1><Link className="primary-button" to="/">新建草稿</Link></header>
      <div className="v2-card-list">
        {drafts.map((draft) => (
          <article className="v2-card" key={draft.session_id}>
            <header><h2>{draft.name}</h2><span>草稿计划</span></header>
            <dl className="v2-kv">
              <dt>当前步骤</dt><dd>{draft.current_step}</dd>
              <dt>项目大纲是否已确认</dt><dd>{draft.outline_confirmed ? '是' : '否'}</dd>
              <dt>最近更新时间</dt><dd>{fmt(draft.updated_at)}</dd>
              <dt>当前模型</dt><dd>{draft.model}</dd>
            </dl>
            <footer>
              <Link className="primary-button" to={`/draft/${draft.session_id}/clarify`}>继续编辑</Link>
              <button className="danger-button" onClick={() => void remove(draft.session_id)}>删除草稿</button>
            </footer>
          </article>
        ))}
      </div>
    </section>
  );
}

export function DraftClarifyV2() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState<any>(null);
  const [calls, setCalls] = useState<any[]>([]);
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [submittedRound, setSubmittedRound] = useState<number | null>(null);
  const [autoAsked, setAutoAsked] = useState(false);
  const load = useCallback(async () => {
    if (!sessionId) return;
    const next = await v2Api.getSession(sessionId);
    setSession(next);
    setCalls(await v2Api.modelCalls(sessionId).catch(() => []));
  }, [sessionId]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    // Only auto-ask on first ever mount for a brand-new draft (no
    // planner question AND status === 'draft').  We deliberately do
    // NOT auto-retry on session refresh / failed sessions — those
    // require an explicit user click on "继续分析".  See the spec in
    // run_reports/2026-07-06-clarify-retry-defense-spec.md.
    if (!sessionId || !session || autoAsked) return;
    const hasPlannerQuestion = (session.messages || []).some((m: any) => m.role === 'planner');
    if (hasPlannerQuestion || session.status !== 'draft') return;
    setAutoAsked(true);
    setBusy(true);
    setError('');
    void v2Api.askOrGenerate(sessionId, false)
      .then(async () => { await load(); })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => { setBusy(false); });
  }, [autoAsked, load, session, sessionId]);
  const round = Math.max(0, Number(session?.clarification_round || 0));
  const clarifyStatus: string = session?.clarify_status || 'awaiting_answer';
  const currentQuestion: string = (session?.current_question || '').toString();

  // ── Display priority for the active question area.  The page must
  // never fall back to a historical planner message; once the
  // question has been answered (current_question nulled), the page
  // shows one of three explicit transition states.
  type DisplayKind =
    | 'awaiting_answer'
    | 'sufficient'
    | 'analyzing'
    | 'failed'
    | 'submitted'
    | 'empty_initial';
  let display: DisplayKind = 'empty_initial';
  let displayText = '';
  if (clarifyStatus === 'sufficient' || session?.status === 'awaiting_human_review') {
    display = 'sufficient';
    displayText = '需求信息已足够，无需补充，可继续生成项目大纲。';
  } else if (clarifyStatus === 'failed') {
    display = 'failed';
    displayText = '本次澄清未形成有效业务问题，请重试。';
  } else if (submittedRound !== null && submittedRound === round) {
    display = 'submitted';
    displayText = `第 ${round} 轮回答已保存，正在生成下一问题`;
  } else if (currentQuestion) {
    display = 'awaiting_answer';
    displayText = currentQuestion;
  } else if (clarifyStatus === 'analyzing' || busy) {
    display = 'analyzing';
    displayText = '正在根据你的回答生成下一步问题。';
  } else {
    display = 'empty_initial';
    displayText = '系统正在准备第一个澄清问题。';
  }

  const shouldPollClarify =
    !!sessionId &&
    !currentQuestion &&
    clarifyStatus !== 'sufficient' &&
    clarifyStatus !== 'failed' &&
    session?.status !== 'awaiting_human_review' &&
    (display === 'empty_initial' || display === 'analyzing' || display === 'submitted' || busy);
  useEffect(() => {
    if (!shouldPollClarify) return;
    let cancelled = false;
    const poll = () => {
      if (!cancelled) void load().catch(() => undefined);
    };
    poll();
    const timer = window.setInterval(poll, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [load, shouldPollClarify]);

  const systemMessage = [...(session?.messages || [])].reverse().find((m: any) => m.role === 'system')?.content || '';

  const submit = async (force = false) => {
    if (!sessionId) return;
    setBusy(true);
    setError('');
    setSubmittedRound(round);
    try {
      if (answer.trim()) {
        try {
          await v2Api.addAnswer(sessionId, round, answer.trim());
        } catch (e: unknown) {
          setSubmittedRound(null);
          const msg = e instanceof Error ? e.message : String(e);
          if (/第\s*\d+\s*轮已提交回答/.test(msg)) {
            setError(msg);
            return;
          }
          setError(msg);
          return;
        }
      }
      const next = await v2Api.askOrGenerate(sessionId, force);
      if (force && next.status === 'awaiting_human_review') {
        navigate(`/draft/${sessionId}/outline`);
        return;
      }
      setAnswer('');
      setSubmittedRound(null);
      await load();
    } catch (e) {
      setSubmittedRound(null);
      setError(e instanceof Error ? e.message : String(e));
      await load().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  const retryContinue = async () => {
    // User explicitly clicks "继续分析".  Same code path as
    // askOrGenerate(false) — but made discoverable as a separate
    // action when the session is in failed / submitted waiting
    // state.  This does NOT loop: a single click → one HTTP call.
    if (!sessionId) return;
    setBusy(true);
    setError('');
    try {
      await v2Api.askOrGenerate(sessionId, false);
      setSubmittedRound(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      await load().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  const clarifyCall = calls.find((c) => c.purpose === 'clarify') || calls[0];
  const clarifyModelText = clarifyCall
    ? `${clarifyCall.provider}/${clarifyCall.model} · ${clarifyCall.status}`
    : '等待首次模型调用';

  return (
    <section className="v2-page narrow">
      <header className="v2-title"><h1>需求澄清</h1><Link className="ghost-button" to="/drafts">返回草稿计划</Link></header>
      <section className="v2-panel">
        <p className="v2-step">
          {display === 'sufficient'
            ? '需求信息已足够，无需补充，可继续生成项目大纲。'
            : display === 'awaiting_answer' || display === 'submitted' || (display === 'analyzing' && round > 0)
              ? `第 ${Math.max(1, round)} / ${session?.clarification_max_rounds || 3} 轮`
              : display === 'failed' ? '未形成有效澄清问题' : '需求澄清'}
        </p>
        <div className="v2-callout">
          <strong>本次需求澄清模型</strong>
          <span>{clarifyModelText}</span>
          <strong>当前状态</strong>
          <span>{
            display === 'submitted' ? '回答已保存,正在生成下一问题'
              : display === 'sufficient' ? '信息已足够'
              : display === 'analyzing' ? '正在分析'
              : display === 'failed' ? '本次澄清失败'
              : display === 'empty_initial' ? '准备澄清问题'
              : '等待用户回答'
          }</span>
        </div>
        <h2 data-testid="clarify-current-question">{displayText}</h2>
        {systemMessage ? (
          <p className="muted-text" data-testid="clarify-system-message">
            <strong>系统提示:</strong>{systemMessage}
          </p>
        ) : null}
        {display === 'awaiting_answer' ? (
          <>
            <textarea
              rows={5}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="写下你的回答,或采用推荐答案"
              data-testid="clarify-answer-input"
            />
            <div className="v2-action-row">
              <button className="ghost-button" onClick={() => setAnswer('按系统推荐范围继续，后续在项目大纲页调整。')}>使用推荐答案</button>
              <button className="ghost-button" disabled={busy} data-testid="clarify-submit" onClick={() => void submit(false)}>提交并继续澄清</button>
              <button className="primary-button" disabled={busy} onClick={() => navigate(`/draft/${sessionId}/outline`)}>信息足够，进入项目大纲生成</button>
            </div>
          </>
        ) : null}
        {display === 'submitted' ? (
          <div className="v2-action-row" data-testid="clarify-submitted-actions">
            <span className="muted-text">已保存,系统正在生成下一问题(可能 5-15 秒)。请勿重复提交。</span>
            <button className="ghost-button" disabled={busy} onClick={() => void load()}>刷新状态</button>
          </div>
        ) : null}
        {display === 'sufficient' ? (
          <div className="v2-action-row" data-testid="clarify-sufficient-actions">
            <span>需求信息已足够，无需补充，可继续生成项目大纲。</span>
            <button className="primary-button" disabled={busy} data-testid="clarify-go-outline" onClick={() => navigate(`/draft/${sessionId}/outline`)}>进入项目大纲生成</button>
          </div>
        ) : null}
        {display === 'analyzing' ? (
          <div className="v2-action-row">
            <span className="muted-text">系统正在分析。请等待当前请求完成。</span>
            <button className="ghost-button" disabled={busy} onClick={() => void load()}>刷新状态</button>
          </div>
        ) : null}
        {display === 'failed' ? (
          <div className="v2-action-row" data-testid="clarify-failed-actions">
            <p className="error-banner">本次澄清未形成有效业务问题:{session?.last_error || '模型本轮未能形成合法业务结果。'}你可以重试,或直接进入项目大纲。</p>
            <button className="primary-button" disabled={busy} data-testid="clarify-retry-continue" onClick={() => void retryContinue()}>继续分析</button>
            <button className="ghost-button" disabled={busy} onClick={() => navigate(`/draft/${sessionId}/outline`)}>直接进入项目大纲</button>
          </div>
        ) : null}
        {error ? <p className="error-banner" data-testid="clarify-error-banner">{error}</p> : null}
      </section>
    </section>
  );
}

export function DraftOutlineV2() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { models } = useCapabilityOptions('project_outline');
  const modelAssignment = useModelAssignment();
  // Legacy draft-plan is kept only as a display fallback (e.g. for
  // the technical-details panel).  Persistence is driven through
  // ``savedBlueprint`` (GET /blueprint) and ``v2Api.saveOutline``.
  const { plan } = useDraftPlan(sessionId);
  const [savedBlueprint, setSavedBlueprint] = useState<any | null>(null);
  const [draftOutline, setDraftOutline] = useState<any | null>(null);
  const [viewBlueprint, setViewBlueprint] = useState<any | null>(null);
  const [gateA, setGateA] = useState<{ passed: boolean; issues: string[] } | null>(null);
  const [calls, setCalls] = useState<any[]>([]);
  const [outlineModelId, setOutlineModelId] = useState('');
  const [changeRequest, setChangeRequest] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [confirmedRisks, setConfirmedRisks] = useState<Set<string>>(new Set());

  const loadSaved = useCallback(async () => {
    if (!sessionId) return;
    try {
      const bp = await v2Api.getBlueprint(sessionId);
      setSavedBlueprint(bp);
      setViewBlueprint(null);
      setDraftOutline(normalizeBlueprintToOutline(bp));
      setGateA(bp.quality_gate);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    }
  }, [sessionId]);
  useEffect(() => { void loadSaved(); }, [loadSaved]);
  useEffect(() => { if (sessionId) void v2Api.modelCalls(sessionId).then(setCalls).catch(() => setCalls([])); }, [sessionId]);
  useEffect(() => {
    if (!plan) return;
    setOutlineModelId((v) => v || (isUuidLike(plan.models.outline_model) ? plan.models.outline_model : ''));
  }, [plan]);
  useEffect(() => {
    const selected = pickPreferredModel(models, modelAssignment, 'planner');
    if (selected) setOutlineModelId((v) => v || selected.id);
  }, [models, modelAssignment]);

  if (!savedBlueprint && !draftOutline && !loadError) {
    return <div className="empty-state">读取项目大纲...</div>;
  }
  const outline = draftOutline ?? (savedBlueprint ? normalizeBlueprintToOutline(savedBlueprint) : null);
  const viewOutline = viewBlueprint ? normalizeBlueprintToOutline(viewBlueprint) : null;

  const regenerate = async () => {
    if (!sessionId) return;
    setBusy(true);
    setActionError('');
    try {
      const gateIssues = (gateA?.issues || []).map((item) => String(item)).filter(Boolean);
      const gateDetails = Array.isArray(savedBlueprint?.quality_gate?.issue_details)
        ? savedBlueprint.quality_gate.issue_details
        : [];
      const selectedOutlineModelId = outlineModelId || pickPreferredModel(models, modelAssignment, 'planner')?.id || '';
      if (!selectedOutlineModelId) {
        setActionError('系统设置中没有可用于整理项目大纲的已配置模型。');
        return;
      }
      const repairInstructions = gateDetails.length
        ? gateDetails.map((item: any, idx: number) => {
            const human = humanizeGateAIssue(item);
            return [
              `${idx + 1}. ${human.title}`,
              `位置：${human.location}`,
              `为什么不过：${human.reason}`,
              human.source ? `当前写法：${human.source}` : '',
              `必须改成类似：${human.example}`,
            ].filter(Boolean).join('\n');
          }).join('\n\n')
        : gateIssues.join('\n');
      const clarificationContext = [
        plan?.requirement ? `当前需求：${plan.requirement}` : '',
        ...(Array.isArray(calls) ? calls.map((call: any) => call?.purpose || '').filter(Boolean).map((purpose: string) => `模型调用：${purpose}`) : []),
      ].filter(Boolean);
      await v2Api.generateBlueprint(sessionId, {
        planning_model_id: selectedOutlineModelId,
        change_request: [
          changeRequest.trim(),
          repairInstructions
            ? `本次重新生成必须逐条修复以下未通过问题，不允许只改标题或重复原文：\n${repairInstructions}`
            : '',
        ].filter(Boolean).join('\n\n'),
        gate_a_issues: repairInstructions ? [repairInstructions] : gateIssues,
        raw_requirement: plan?.requirement || '',
        clarification_context: clarificationContext,
      });
      await loadSaved();
      setChangeRequest('');
      setCalls(await v2Api.modelCalls(sessionId).catch(() => []));
      // After a successful regeneration, if Gate A passes AND no risk
      // items are awaiting human confirmation, auto-confirm and jump to
      // the task plan step.  The previous flow forced the user to click
      // "重新生成完整项目大纲" and then "确认项目大纲,进入任务拆解" in
      // sequence — a "需要点击两次" workflow that did not even advance
      // when the second click returned a 409 from Gate A.  We only auto-
      // advance when the user has nothing left to review (no risks
      // requiring human confirmation).
      const fresh = await v2Api.getBlueprint(sessionId);
      if (fresh && fresh.quality_gate?.passed && !hasOutstandingRiskConfirmations(fresh)) {
        await v2Api.confirmBlueprint(sessionId);
        navigate(`/draft/${sessionId}/task-plan`);
        return;
      }
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!outline) {
    const outlineModel = pickPreferredModel(models, modelAssignment, 'planner');
    return (
      <section className="v2-page" data-testid="v2-outline-empty">
        <header className="v2-title"><h1>准备项目大纲检查</h1></header>
        <section className="v2-panel">
          <p>如果你是从“已有大纲”或“接手 GitHub 项目”进入，这一步会把已有内容整理成可检查、可编辑的大纲；不是重新让你做需求澄清。</p>
          <div className="v2-callout">
            <strong>项目大纲生成模型</strong>
            <span data-testid="v2-outline-model-status">{outlineModel ? modelChoiceText(outlineModel) : '未配置可用模型'}</span>
            <strong>执行策略</strong>
            <span>模型必须基于你提供的已有大纲或仓库文档整理，不得从零幻想项目。</span>
          </div>
          {actionError ? <p className="error-banner">生成失败：{actionError}</p> : null}
          <button
            className="primary-button"
            disabled={busy || !outlineModel}
            onClick={() => void regenerate()}
          >{busy ? '整理中…' : '导入并检查项目大纲'}</button>
        </section>
      </section>
    );
  }
  const outlineCall = calls.find((c) => c.purpose === 'blueprint') || calls.find((c) => c.purpose === 'task_plan');
  const outlineModel = pickPreferredModel(models, modelAssignment, 'planner');
  const chosenOutlineModel = modelChoiceText(outlineModel) || selectedModelText(models, outlineModelId, plan?.models?.outline_model);
  const quality = savedBlueprint?.is_confirmed
    ? { passed: true, issues: [] as string[] }
    : (gateA ?? { passed: false, issues: ['尚未保存'] });
  const parsedRisks = parseRisksField(outline?.risks);
  const issueDetails = Array.isArray(savedBlueprint?.quality_gate?.issue_details)
    ? savedBlueprint.quality_gate.issue_details
    : [];
  const humanIssues = issueDetails.length
    ? issueDetails.map(humanizeGateAIssue)
    : (quality.issues || []).map((issue: string) => humanizeGateAIssue(issue));
  const allRisksConfirmed = parsedRisks.every((risk: any) => {
    if (!risk || typeof risk !== 'object' || Array.isArray(risk)) return true;
    if (risk.confirmation_required !== true) return true;
    const riskId = risk.id ?? `risk-${parsedRisks.indexOf(risk)}`;
    return confirmedRisks.has(riskId);
  });

  const saveDraft = async () => {
    if (!sessionId || !draftOutline) return;
    setSaving(true);
    setActionError('');
    try {
      const resp = await v2Api.saveOutline(sessionId, outlineToPayload(draftOutline));
      setGateA(resp.quality_gate);
      await loadSaved();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };
  const updatePhase = (index: number, patch: Record<string, unknown>) => {
    const phases = Array.isArray(draftOutline?.phases) ? [...draftOutline.phases] : [];
    phases[index] = { ...(phases[index] || {}), ...patch };
    setDraftOutline({ ...draftOutline, phases });
  };
  const openBlueprintVersion = async (blueprintId: string) => {
    if (!sessionId) return;
    setActionError('');
    try {
      setViewBlueprint(await v2Api.getBlueprintVersion(sessionId, blueprintId));
    } catch (e) {
      setActionError(userFacingError(e));
    }
  };
  const versionPanel = savedBlueprint?.versions?.length ? (
    <section className="v2-panel" data-testid="v2-outline-version-history">
      <h2>项目大纲版本历史</h2>
      <div className="v2-action-row">
        {savedBlueprint.versions.map((item: any) => (
          <button
            key={item.id}
            className={item.id === savedBlueprint.id ? 'primary-button' : 'ghost-button'}
            disabled={busy}
            onClick={() => void openBlueprintVersion(item.id)}
          >v{item.version} · {item.name}</button>
        ))}
        {viewBlueprint ? <button className="ghost-button" onClick={() => setViewBlueprint(null)}>关闭旧版本预览</button> : null}
      </div>
      {viewOutline ? (
        <article className="v2-card" data-testid="v2-outline-version-preview">
          <h3>只读预览：v{viewBlueprint.version} · {viewOutline.name}</h3>
          <p><strong>目标：</strong>{viewOutline.overall_goal}</p>
          <p><strong>范围：</strong>{(viewOutline.scope || []).join('；')}</p>
          <strong>阶段</strong>
          <ul>{(viewOutline.phases || []).map((stage: any, idx: number) => <li key={`${stage.name}-${idx}`}>{stage.name}：{stage.phase_goal}</li>)}</ul>
        </article>
      ) : null}
    </section>
  ) : null;

  if (!quality.passed) {
    return (
      <section className="v2-page" data-testid="v2-outline-page">
        <header className="v2-title"><div><span>当前步骤：项目大纲检查未通过</span><h1>{outline.name || '尚未生成'}</h1></div><strong>需要补充后才能继续</strong></header>
        <section className="v2-panel">
          <div className="v2-callout">
            <strong>当前项目大纲模型</strong>
            <span data-testid="v2-outline-available-models">
              {chosenOutlineModel || '当前没有已配置且可用于本步骤的模型。'}
            </span>
            <strong>为什么没通过</strong>
            <span data-testid="v2-outline-quality">
              {humanIssues.length
                ? humanIssues.map((item: any) => item.title).join('；')
                : '项目大纲还缺少可检查的完成标准。'}
            </span>
            <strong>怎么继续</strong>
            <span>优先直接修改下面对应内容并保存；也可以点击“按问题重新生成”，系统会把这些未通过原因一起交给模型修复。</span>
          </div>
        </section>
        {versionPanel}
        <section className="v2-panel" data-testid="v2-outline-gate-a-issues">
          <h2>需要修改的地方</h2>
          {humanIssues.length ? humanIssues.map((item: any, idx: number) => (
            <article className="v2-card" key={`${item.location}-${idx}`}>
              <h3>{item.title}</h3>
              <p><strong>位置：</strong>{item.location}</p>
              <p><strong>原因：</strong>{item.reason}</p>
              <p><strong>当前写法：</strong>{item.source || '这里还没有写清楚。'}</p>
              <p className="muted-text"><strong>建议写法：</strong>{item.example}</p>
            </article>
          )) : <EditableList items={quality.issues || []} />}
        </section>
        <section className="v2-panel" data-testid="v2-outline-inline-editor">
          <h2>直接编辑大纲</h2>
          <div className="v2-grid two">
            <label><span>目标用户</span><textarea rows={3} value={outline.target_users} onChange={(e) => setDraftOutline({ ...draftOutline, target_users: e.target.value })} /></label>
            <label><span>项目定位</span><textarea rows={3} value={outline.positioning} onChange={(e) => setDraftOutline({ ...draftOutline, positioning: e.target.value })} /></label>
            <label><span>项目整体目标</span><textarea rows={3} value={outline.overall_goal} onChange={(e) => setDraftOutline({ ...draftOutline, overall_goal: e.target.value })} /></label>
            <label><span>业务背景</span><textarea rows={3} value={outline.business_context} onChange={(e) => setDraftOutline({ ...draftOutline, business_context: e.target.value })} /></label>
          </div>
          <div className="v2-grid two">
            <div><strong>项目范围</strong><EditableList items={outline.scope} onChange={(items) => setDraftOutline({ ...draftOutline, scope: items })} /></div>
            <div><strong>项目整体验收标准</strong><EditableList items={outline.project_acceptance || []} onChange={(items) => setDraftOutline({ ...draftOutline, project_acceptance: items })} /></div>
          </div>
          {(outline.phases || []).map((stage: any, idx: number) => (
            <article className="v2-card" key={`${stage.id || idx}`}>
              <h3>阶段 {idx + 1}：{stage.name || '未命名'}</h3>
              <label><span>阶段名称</span><textarea rows={2} value={stage.name || ''} onChange={(e) => updatePhase(idx, { name: e.target.value })} /></label>
              <label><span>阶段目标</span><textarea rows={2} value={stage.phase_goal || ''} onChange={(e) => updatePhase(idx, { phase_goal: e.target.value })} /></label>
              <label><span>模块范围</span><textarea rows={2} value={stage.module_scope || ''} onChange={(e) => updatePhase(idx, { module_scope: e.target.value })} /></label>
              <strong>交付物</strong><EditableList items={stage.deliverables || []} onChange={(items) => updatePhase(idx, { deliverables: items })} />
              <strong>阶段验收标准</strong><EditableList items={stage.stage_acceptance || []} onChange={(items) => updatePhase(idx, { stage_acceptance: items })} />
              <strong>风险与待确认项</strong><EditableList items={stage.risks || []} onChange={(items) => updatePhase(idx, { risks: items })} />
            </article>
          ))}
          <p className="muted-text">保存后会写入新的项目大纲版本，并重新运行 Gate A；旧版本仍可在版本历史中回看。</p>
        </section>
        <section className="v2-panel">
          <h2>修改意见</h2>
          <textarea
            rows={6}
            value={changeRequest}
            onChange={(e) => setChangeRequest(e.target.value)}
            placeholder="写下你希望补充、删除或改写的项目大纲意见"
            data-testid="v2-outline-change-request"
          />
          {actionError ? <p className="error-banner">保存失败:{actionError}</p> : null}
          <p className="muted-text" data-testid="v2-outline-auto-upgrade-state">
            大纲没通过时不会继续拆任务，避免后面开发跑偏。你可以手动改，也可以让模型按上面的问题重新生成。
          </p>
        </section>
        <footer className="v2-sticky-actions">
          <Link className="ghost-button" to={`/draft/${sessionId}/clarify`}>返回补充需求</Link>
          <button
            className="ghost-button"
            data-testid="v2-outline-regenerate-only"
            disabled={busy || !outlineModel}
            onClick={() => void regenerate()}
          >{busy ? '正在按问题重新生成…' : '按问题重新生成'}</button>
          <button
            className="primary-button"
            data-testid="v2-outline-save-draft"
            disabled={saving || !draftOutline}
            onClick={() => void saveDraft()}
          >{saving ? '保存中…' : '保存修改并重新检查'}</button>
        </footer>
      </section>
    );
  }

  return (
    <section className="v2-page" data-testid="v2-outline-page">
      <header className="v2-title"><div><span>当前步骤:项目大纲确认</span><h1>{outline.name}</h1></div><strong>{savedBlueprint?.is_confirmed ? '项目大纲已确认' : '尚未创建正式项目'}</strong></header>
      <section className="v2-panel">
        <div className="v2-callout">
          <strong>项目大纲生成模型</strong>
          <span>{outlineCall ? `${outlineCall.provider}/${outlineCall.model} · ${outlineCall.status}` : chosenOutlineModel}</span>
          <strong>执行策略</strong>
          <span data-testid="v2-outline-available-models">
            {outlineModel ? '优先使用系统设置页的角色默认模型；不会静默切换到未配置模型。' : '当前没有已配置且可用于本步骤的模型。'}
          </span>
          <strong>质量门结果</strong>
          <span data-testid="v2-outline-quality">
            {quality.passed ? '项目大纲质量检查:通过' : `项目大纲质量检查:未通过(${quality.issues?.join('; ') ?? ''})`}
          </span>
        </div>
        <p className="muted-text" data-testid="v2-outline-auto-upgrade-state">
          项目大纲生成失败时保留当前状态并提示重试；不会退回澄清，也不会静默切换执行模型。
        </p>
      </section>
      {versionPanel}
      <section className="v2-panel"><h2>项目概要</h2><div className="v2-grid two">
        <label><span>目标用户</span><textarea rows={3} value={outline.target_users} onChange={(e) => setDraftOutline({ ...draftOutline, target_users: e.target.value })} /></label>
        <label><span>项目定位</span><textarea rows={3} value={outline.positioning} onChange={(e) => setDraftOutline({ ...draftOutline, positioning: e.target.value })} /></label>
        <label><span>项目整体目标</span><textarea rows={3} value={outline.overall_goal} onChange={(e) => setDraftOutline({ ...draftOutline, overall_goal: e.target.value })} /></label>
        <label><span>业务背景</span><textarea rows={3} value={outline.business_context} onChange={(e) => setDraftOutline({ ...draftOutline, business_context: e.target.value })} /></label>
      </div></section>
      <section className="v2-panel"><h2>范围与边界</h2><div className="v2-grid two">
        <div><strong>项目范围</strong><EditableList items={outline.scope} /></div>
        <div><strong>非范围</strong><EditableList items={outline.out_of_scope} /></div>
      </div></section>
      <section className="v2-panel">
        <h2>风险与待确认项</h2>
        {parseRisksField(outline.risks).length === 0 ? <p className="muted-text">暂无风险项</p> : parseRisksField(outline.risks).map((rawRisk: any, idx: number) => {
          // If risk is a JSON string, parse it first
          let risk = rawRisk;
          if (typeof risk === 'string' && risk.trim().startsWith('{')) {
            try { risk = JSON.parse(risk); } catch { /* keep as string */ }
          }
          const isObj = risk && typeof risk === 'object' && !Array.isArray(risk);
          const riskId = isObj ? (risk.id ?? `risk-${idx}`) : `risk-${idx}`;
          const needsConfirm = isObj && risk.confirmation_required === true;
          const isConfirmed = confirmedRisks.has(riskId);
          return (
            <article key={riskId} className="v2-risk-card" style={{ border: '1px solid #ddd', borderRadius: 6, padding: '12px 16px', marginBottom: 8, background: needsConfirm && !isConfirmed ? '#fff8e1' : '#fff' }}>
              {isObj ? (
                <>
                  {risk.flag ? <strong style={{ display: 'block', color: '#b71c1c', marginBottom: 4 }}>{risk.flag}</strong> : null}
                  {risk.phase ? <span style={{ fontSize: '0.85em', color: '#666' }}>所属阶段：{risk.phase}</span> : null}
                  {(risk.description || risk.risk) ? <p style={{ margin: '6px 0 0' }}>{risk.description || risk.risk}</p> : null}
                  {risk.mitigation ? <p style={{ margin: '4px 0 0', color: '#555' }}>应对措施：{risk.mitigation}</p> : null}
                  {risk.id ? <span style={{ fontSize: '0.8em', color: '#999' }}>{risk.id}</span> : null}
                </>
              ) : (
                <p style={{ margin: 0 }}>{normalizeRiskItem(rawRisk)}</p>
              )}
              {needsConfirm ? (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={isConfirmed}
                    onChange={(e) => {
                      const next = new Set(confirmedRisks);
                      if (e.target.checked) next.add(riskId); else next.delete(riskId);
                      setConfirmedRisks(next);
                    }}
                  />
                  <span>{isConfirmed ? '已确认此风险' : '确认此风险后可继续'}</span>
                </label>
              ) : null}
            </article>
          );
        })}
      </section>
      <section className="v2-panel"><h2>阶段总览</h2><div className="v2-stage-strip">
        {(outline.phases ?? []).map((stage: any) => {
          const stageRisks = parseRisksField(stage.risks);
          return <article className="v2-stage-card" key={stage.id ?? stage.phase_number}>
          <header><span>阶段 {stage.phase_number ?? stage.order}</span><h3>{stage.name}</h3></header>
          <dl className="v2-kv"><dt>阶段目标</dt><dd>{stage.phase_goal ?? stage.goal}</dd><dt>模块范围</dt><dd>{stage.module_scope}</dd><dt>阶段交付物</dt><dd>{parseRisksField(stage.deliverables).map(normalizeRiskItem).join('；') || '待确认'}</dd></dl>
          {stageRisks.length > 0 ? (
            <div style={{ marginTop: 8 }}>
              <strong style={{ fontSize: '0.9em', color: '#333' }}>阶段风险</strong>
              {stageRisks.map((risk: any, ri: number) => {
                const isObj = risk && typeof risk === 'object' && !Array.isArray(risk);
                return (
                  <div key={ri} style={{ border: '1px solid #e0e0e0', borderRadius: 4, padding: '8px 12px', marginTop: 6, background: '#fafafa', fontSize: '0.9em' }}>
                    {isObj ? (<>
                      {risk.flag ? <div style={{ color: '#b71c1c', fontWeight: 600, marginBottom: 2 }}>{risk.flag}</div> : null}
                      {(risk.description || risk.risk) ? <div><span style={{ color: '#666' }}>风险说明：</span>{risk.description || risk.risk}</div> : null}
                      {risk.mitigation ? <div><span style={{ color: '#666' }}>应对措施：</span>{risk.mitigation}</div> : null}
                      {risk.phase ? <div><span style={{ color: '#666' }}>所属阶段：</span>{risk.phase}</div> : null}
                      {risk.id ? <div style={{ color: '#999', fontSize: '0.85em' }}>{risk.id}</div> : null}
                    </>) : (
                      <div>{normalizeRiskItem(risk)}</div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <dl className="v2-kv" style={{ marginTop: 4 }}><dt>阶段风险</dt><dd>待确认</dd></dl>
          )}
          <dl className="v2-kv" style={{ marginTop: 4 }}><dt>阶段验收目标</dt><dd>{(stage.stage_acceptance ?? stage.acceptance_criteria?.map((c: any) => c.criterion) ?? []).join('；') || '待确认'}</dd></dl>
        </article>;
        })}
      </div></section>
      <section className="v2-panel">
        <h2>修改意见</h2>
        <textarea rows={4} value={changeRequest} onChange={(e) => setChangeRequest(e.target.value)} placeholder="写下你希望补充、删除或改写的项目大纲意见" />
        {actionError ? <p className="error-banner">保存失败:{actionError}</p> : null}
      </section>
      <footer className="v2-sticky-actions">
        <Link className="ghost-button" to={`/draft/${sessionId}/clarify`}>返回补充需求</Link>
        <button className="ghost-button" data-testid="v2-outline-regenerate" disabled={busy || !outlineModel} onClick={() => void regenerate()}>{busy ? '重新生成中…' : '按修改意见重新生成项目大纲'}</button>
        {savedBlueprint?.is_confirmed ? (
          <button
            className="primary-button"
            data-testid="v2-outline-go-taskplan"
            onClick={() => navigate(`/draft/${sessionId}/task-plan`)}
          >进入任务拆解</button>
        ) : (<>
          <button
            className="primary-button"
            data-testid="v2-outline-save-draft"
            disabled={saving || !draftOutline}
            onClick={() => void saveDraft()}
          >{saving ? '保存中…' : '保存当前编辑到后端'}</button>
          <button
            className={quality.passed && allRisksConfirmed ? 'primary-button' : 'ghost-button'}
            data-testid="v2-outline-confirm"
            disabled={!quality.passed || !allRisksConfirmed || busy || saving}
            onClick={async () => {
              if (!quality.passed || !allRisksConfirmed) return;
              try {
                if (draftOutline) {
                  await v2Api.saveOutline(sessionId!, outlineToPayload(draftOutline));
                }
                await v2Api.confirmBlueprint(sessionId!);
                navigate(`/draft/${sessionId}/task-plan`);
              } catch (e) {
                setActionError(e instanceof Error ? e.message : String(e));
              }
            }}
          >{!allRisksConfirmed ? '请先确认所有标记为"需要确认"的风险项' : quality.passed ? '确认项目大纲,进入任务拆解' : '请先保存并通过 Gate A'}</button>
        </>)}
      </footer>
    </section>
  );
}

function GateBPanel({ gateB, allDrafts }: { gateB: any; allDrafts: any[] }) {
  const categories = gateB?.categories ?? {};
  const issues: any[] = Array.isArray(gateB?.issues) ? gateB.issues : [];
  const categoryTotal = Object.values(categories).reduce((sum: number, value: any) => sum + Number(value || 0), 0);
  const passed = taskPlanGatePassed(gateB);
  // Real-world reason: every draft is save_pending (no executor). The
  // backend gate counts zero issues but the page should still tell the
  // user *why* they cannot confirm.
  const allSavePending = allDrafts.length > 0 && allDrafts.every((d) => d.executor_agent === 'save_pending');
  const noExecutor = allSavePending;
  const totalIssues = Math.max(categoryTotal, issues.length);
  const coverage = gateB?.coverage ?? { covered_count: 0, total_count: 0, uncovered_stages: [] };
  const uncoveredStages: Array<{ id: string; phase_number: number; name: string }> = Array.isArray(coverage.uncovered_stages) ? coverage.uncovered_stages : [];
  return (
    <div data-testid="v2-task-plan-gate-b">
      <strong>任务计划检查</strong>
      <span>
        {passed
          ? <span className="ok-tag">通过</span>
          : <span className="error-banner">未通过</span>}
      </span>
      <div data-testid="v2-task-plan-coverage">
        <strong>阶段覆盖</strong>：{coverage.covered_count ?? 0} / {coverage.total_count ?? 0}
      </div>
      {uncoveredStages.length > 0 ? (
        <div className="error-banner" data-testid="v2-task-plan-uncovered-stages">
          <strong>未覆盖阶段</strong>
          <ul>{uncoveredStages.map((stage) => <li key={stage.id}>阶段 {stage.phase_number}：{stage.name}</li>)}</ul>
        </div>
      ) : null}
      {!passed ? (
        <ul className="v2-callout-list" data-testid="v2-task-plan-gate-b-reasons">
          <li><strong>任务内容不完整</strong>：{categories.fields ?? 0} 条</li>
          <li><strong>执行模型或代理不匹配</strong>：{categories.whitelist ?? 0} 条
            {noExecutor ? (
              <em> — 当前任务还没有指定可执行代理，因此不能确认。</em>
            ) : null}
          </li>
          <li><strong>超出项目范围</strong>：{categories.scope ?? 0} 条</li>
          <li><strong>需求未覆盖</strong>：{categories.coverage ?? 0} 条</li>
          <li><strong>任务依赖有问题</strong>：{categories.graph ?? 0} 条</li>
          <li><strong>合计</strong>：{totalIssues} 条需要处理</li>
          {noExecutor && totalIssues === 0 ? (
            <li className="v2-callout-list-warning">
              <strong>说明</strong>：所有任务都还没有执行代理。请选择已配置的执行模型，应用到全部任务后再确认。
            </li>
          ) : null}
        </ul>
      ) : null}
      {!passed && issues.length > 0 ? (
        <details>
          <summary>需要修改的任务问题（{issues.length} 条）</summary>
          <ul>
            {issues.slice(0, 30).map((iss: any, idx: number) => {
              const human = humanizeGateBIssue(iss);
              return (
                <li key={idx}>
                  <strong>{human.title}</strong>：{human.detail}
                  <div>问题类型：{human.problemType}</div>
                  {human.requirement ? <div>对应需求：{human.requirement}</div> : null}
                  {human.sourceRequirement && human.sourceRequirement !== human.requirement ? <div>原始需求：{human.sourceRequirement}</div> : null}
                  <div>对应任务：{human.task || human.relatedTasks || '暂无，建议新增任务'}</div>
                  {human.missingField ? <div>缺失字段：{human.missingField}</div> : null}
                  <div>当前值：{human.current || '（空）'}</div>
                  <div>修正建议：{human.suggestion || human.action}</div>
                  {iss.coverage_reason ? <div>判定原因：{iss.coverage_reason}</div> : null}
                </li>
              );
            })}
          </ul>
        </details>
      ) : null}
      {Array.isArray(gateB?.requirement_trace) && gateB.requirement_trace.length > 0 ? (
        <details>
          <summary>需求→任务→验收追溯（{gateB.requirement_trace.length} 条）</summary>
          <div className="v2-table-wrap">
            <table className="v2-table">
              <thead><tr><th>原始需求</th><th>覆盖状态</th><th>对应任务与验收证据</th><th>缺失字段 / 修正建议</th></tr></thead>
              <tbody>
                {gateB.requirement_trace.map((trace: any, idx: number) => (
                  <tr key={`${trace.requirement || 'requirement'}-${idx}`}>
                    <td>{trace.source_clause || trace.requirement}</td>
                    <td>{trace.covered ? '已覆盖' : '未覆盖'}</td>
                    <td>
                      {Array.isArray(trace.related_tasks) && trace.related_tasks.length > 0 ? trace.related_tasks.map((task: any) => (
                        <div key={task.draft_id || task.title}>
                          <strong>{task.title || '未命名任务'}</strong>
                          {Array.isArray(task.acceptance_criteria) && task.acceptance_criteria.length > 0 ? (
                            <ul>{task.acceptance_criteria.map((criterion: string, criterionIndex: number) => <li key={criterionIndex}>{criterion}</li>)}</ul>
                          ) : <div className="muted-text">验收标准为空</div>}
                        </div>
                      )) : <span className="muted-text">暂无关联任务</span>}
                    </td>
                    <td>
                      {trace.missing_field ? <div>{trace.missing_field}</div> : <div>—</div>}
                      {trace.suggested_fix ? <div>{trace.suggested_fix}</div> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      ) : null}
    </div>
  );
}


export function DraftTaskPlanV2() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { models: taskPlanningModels } = useCapabilityOptions('task_planning');
  const { models: executionModelRows } = useCapabilityOptions('real_file_write');
  const modelAssignment = useModelAssignment();
  // legacy draft-plan used ONLY for display fallback (technical details panel)
  const { plan } = useDraftPlan(sessionId);
  const [drafts, setDrafts] = useState<TaskDraft[]>([]);
  const [taskPlanStatus, setTaskPlanStatus] = useState<'not_generated' | 'draft_ready' | 'user_confirmed'>('not_generated');
  const [taskPlanRevision, setTaskPlanRevision] = useState(0);
  const [gateB, setGateB] = useState<any>({ passed: false, issues: [], categories: { fields: 0, whitelist: 0, scope: 0, graph: 0 } });
  const [draftError, setDraftError] = useState('');
  const [draftLoading, setDraftLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [applyingRouteDraftId, setApplyingRouteDraftId] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');
  const [taskPlanningModelId, setTaskPlanningModelId] = useState('');
  const [bulkRecommendedCodeModel, setBulkRecommendedCodeModel] = useState('');
  const [bulkExecutorAgent, setBulkExecutorAgent] = useState('');
  const [bulkBackupRoute, setBulkBackupRoute] = useState('manual_route_switch');
  const [blueprintConfirmed, setBlueprintConfirmed] = useState(false);
  useEffect(() => {
    if (sessionId) {
      v2Api.getBlueprint(sessionId).then((bp: any) => setBlueprintConfirmed(!!bp?.is_confirmed)).catch(() => {});
    }
  }, [sessionId]);
  useEffect(() => {
    if (!plan) return;
    setTaskPlanningModelId((v) => v || (isUuidLike(plan.models.task_plan_model) ? plan.models.task_plan_model : ''));
  }, [plan]);
  useEffect(() => {
    const selected = pickPreferredModel(taskPlanningModels, modelAssignment, 'decomposer');
    if (selected) setTaskPlanningModelId((v) => v || selected.id);
  }, [taskPlanningModels, modelAssignment]);
  useEffect(() => {
    const firstConfigured = drafts.find((d) => d.recommended_code_model || d.executor_agent || d.backup_route);
    if (!firstConfigured) return;
    setBulkRecommendedCodeModel((v) => v || firstConfigured.recommended_code_model || '');
    setBulkExecutorAgent((v) => v || firstConfigured.executor_agent || '');
    setBulkBackupRoute((v) => v || firstConfigured.backup_route || 'manual_route_switch');
  }, [drafts]);
  // Fetch drafts from the persistent API on mount + after every write.
  const loadDrafts = useCallback(async () => {
    if (!sessionId) return;
    setDraftLoading(true);
    setDraftError('');
    try {
      const resp = await v2Api.listTaskDrafts(sessionId);
      setDrafts(resp.drafts);
      setTaskPlanStatus(resp.task_plan_status);
      setTaskPlanRevision(resp.task_plan_revision);
      // Run gate B dry-run so the UI shows current outstanding issues.
      // ``POST /confirm`` returns 409 with a structured ``detail.quality_gate``
      // when drafts are still incomplete; recover that, never the empty
      // placeholder, so the panel reflects real outstanding issues instead
      // of the previous "0 issues in every category" misreport.
      const EMPTY_GATE: TaskPlanQualityGate = {
        passed: false,
        status_text: 'quality_gate_not_loaded',
        issues: [],
        categories: { fields: 0, whitelist: 0, scope: 0, graph: 0 },
        field_mapping: {},
        coverage: { passed: false, covered_count: 0, total_count: 0, covered_stage_ids: [], uncovered_stages: [] },
      };
      if (resp.task_plan_status === 'user_confirmed') {
        setGateB({
          passed: true,
          status_text: '任务计划质量门已通过',
          issues: [],
          categories: { fields: 0, whitelist: 0, scope: 0, graph: 0 },
          field_mapping: {},
          coverage: { passed: true, covered_count: 0, total_count: 0, covered_stage_ids: [], uncovered_stages: [] },
        });
        return;
      }
      const gateResp = await v2Api.taskDraftsQualityGate(sessionId).catch((e: unknown) => {
        if (
          e instanceof Error &&
          typeof (e as unknown as { detail?: unknown }).detail === 'object' &&
          (e as unknown as { detail: { quality_gate?: unknown } }).detail.quality_gate
        ) {
          return {
            quality_gate: (e as unknown as { detail: { quality_gate: TaskPlanQualityGate } })
              .detail.quality_gate,
          };
        }
        return { quality_gate: EMPTY_GATE };
      });
      setGateB(gateResp.quality_gate ?? EMPTY_GATE);
    } catch (e) {
      setDraftError(e instanceof Error ? e.message : String(e));
    } finally {
      setDraftLoading(false);
    }
  }, [sessionId]);
  useEffect(() => { void loadDrafts(); }, [loadDrafts]);
  if (draftLoading && !drafts.length) return <div className="empty-state">读取任务草稿...</div>;
  if (draftError && !drafts.length) {
    return (
      <section className="v2-page">
        <header className="v2-title"><div><span>当前步骤:任务拆解与执行计划确认</span></div><strong>读取失败</strong></header>
        <section className="v2-panel"><p className="error-banner">{draftError}</p></section>
      </section>
    );
  }
  // Outline quality gate hard-block: redirect when gate A fails
  // (but skip for confirmed blueprints — confirmation implies Gate A passed).
  const outlineQualityCheck = plan ? outlineQuality(plan) : { passed: true, reasons: [] };
  if (!outlineQualityCheck.passed && !blueprintConfirmed) {
    return (
      <section className="v2-page" data-testid="v2-task-plan-blocked">
        <header className="v2-title"><div><span>当前步骤:任务拆解与执行计划确认(已阻断)</span><h1>{plan?.project_outline?.name || '未命名'}</h1></div><strong>大纲未通过</strong></header>
        <section className="v2-panel">
          <p className="error-banner" data-testid="v2-task-plan-blocked-reason">
            项目大纲质量门未通过:{outlineQualityCheck.reasons.join('; ')}。请返回项目大纲页修改或重新生成。
          </p>
        </section>
        <footer className="v2-sticky-actions">
          <Link className="primary-button" to={`/draft/${sessionId}/outline`}>返回修复项目大纲</Link>
        </footer>
      </section>
    );
  }
  const taskPlanMiniMax = pickMiniMaxModel(taskPlanningModels);
  const assignedTaskPlanModel = pickAssignedModel(taskPlanningModels, modelAssignment, 'decomposer');
  const executionMiniMax = pickMiniMaxModel(executionModelRows);
  const assignedExecutionModel = pickAssignedModel(executionModelRows, modelAssignment, 'executor');
  const executionModels = compactModels([
    assignedExecutionModel,
    ...executionModelRows,
    executionMiniMax,
  ]).filter(isExecutionReadyModel);
  const selectedTaskPlanModel = pickPreferredModel(taskPlanningModels, modelAssignment, 'decomposer') || taskPlanMiniMax;
  const selectedExecutionModel = assignedExecutionModel && isExecutionReadyModel(assignedExecutionModel)
    ? assignedExecutionModel
    : executionModels[0] || executionMiniMax;
  const bulkSelectedModel = findExecutionModelByValue(bulkRecommendedCodeModel, executionModels);
  const bulkSelectedModelValue = bulkSelectedModel ? executionModelValue(bulkSelectedModel) : '';
  const bulkModelInvalid = !!bulkRecommendedCodeModel && !bulkSelectedModelValue;
  const chosenTaskPlanModel = selectedTaskPlanModel
    ? `${selectedTaskPlanModel.display_name || selectedTaskPlanModel.model}${assignedTaskPlanModel ? '（系统设置默认）' : ''}`
    : selectedModelText(taskPlanningModels, taskPlanningModelId, plan?.models?.task_plan_model);
  const noDraftsYet = drafts.length === 0 && taskPlanStatus === 'not_generated';
  const canGenerateFromBlueprint = !!(plan?.project_outline?.name);
  const gateBReady = taskPlanGatePassed(gateB);
  const coverageReady = gateB?.coverage?.passed === true;
  const canConfirm = !noDraftsYet && drafts.some((d) => d.status !== 'confirmed');
  const createdProjectId = plan?.formal_object_state?.created_project_id;
  const routeEditable = taskPlanStatus === 'draft_ready' && !createdProjectId;

  const generate = async () => {
    if (!sessionId) return;
    setGenerating(true);
    setActionError('');
    try {
      const planningModelId = taskPlanningModelId || selectedTaskPlanModel?.id || null;
      // Force replace so the backend supersedes old drafts and bumps
      // task_plan_revision; without this the endpoint returns 409 once
      // the user has confirmed the plan.
      await v2Api.generateTaskDrafts(sessionId, {
        planning_model_id: planningModelId,
        execution_model: selectedExecutionModel ? executionModelValue(selectedExecutionModel) : null,
        executor_agent: selectedExecutionModel?.execution_adapter || null,
        gate_b_issues: (gateB?.issues || []).map((issue: TaskPlanQualityGate['issues'][number]) => issue.text),
        force_replace_existing: true,
      });
      await loadDrafts();
    } catch (e) {
      // The ApiError message already carries the backend's structured
      // ``[code] detail`` payload, so the page never shows the generic
      // 'Internal Server Error' string when the server returned a real
      // 4xx with detail.
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setGenerating(false);
    }
  };

  const patchDraft = async (draftId: string, patch: Record<string, unknown>) => {
    if (!sessionId) return;
    setActionError('');
    try {
      const resp = await v2Api.patchTaskDraft(sessionId, draftId, {
        ...patch,
        expected_task_plan_revision: taskPlanRevision,
      });
      setDrafts((cur) => cur.map((d) => d.id === resp.draft.id ? resp.draft : d));
      setTaskPlanStatus(resp.task_plan_status);
      setTaskPlanRevision(resp.task_plan_revision);
      if (resp.gate_b) setGateB(resp.gate_b);
      if (resp.warning) setActionError(resp.warning);
      return resp;
    } catch (e) {
      setActionError(userFacingError(e));
      throw e;
    }
  };

  const repairTaskDrafts = async () => {
    if (!sessionId) return;
    setBusy(true);
    setActionError('');
    try {
      const planningModelId = taskPlanningModelId || selectedTaskPlanModel?.id || null;
      const resp = await v2Api.autoFixTaskDrafts(sessionId, {
        planning_model_id: planningModelId,
        execution_model: selectedExecutionModel ? executionModelValue(selectedExecutionModel) : null,
        executor_agent: selectedExecutionModel?.execution_adapter || null,
      });
      setDrafts(resp.drafts);
      setTaskPlanStatus(resp.task_plan_status);
      setTaskPlanRevision(resp.task_plan_revision);
      setGateB(resp.quality_gate ?? { passed: false, issues: [], categories: {} });
      if (!resp.quality_gate?.passed) {
        setActionError(`已生成 revision ${resp.task_plan_revision}，Gate B 仍未通过；请按下方具体问题人工编辑并保存。`);
      }
    } catch (e) {
      setActionError(userFacingError(e));
      await loadDrafts().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  const applyRouteToAllDrafts = async () => {
    if (!sessionId) return;
    const selectedRouteModel = findExecutionModelByValue(bulkRecommendedCodeModel, executionModels);
    if (!selectedRouteModel?.execution_adapter) {
      setActionError('模型配置无效：请重新选择一个已接入真实执行适配器的模型。');
      return;
    }
    setApplyingRouteDraftId('all');
    setActionError('');
    const routePatch = {
      recommended_code_model: executionModelValue(selectedRouteModel),
      executor_agent: selectedRouteModel.execution_adapter,
      backup_route: bulkBackupRoute,
    };
    try {
      let latestGate: TaskPlanQualityGate | null = null;
      let expectedRevision = taskPlanRevision;
      const updated = new Map<string, TaskDraft>();
      for (const draft of drafts) {
        const resp = await v2Api.patchTaskDraft(sessionId, draft.id, {
          ...routePatch,
          expected_task_plan_revision: expectedRevision,
        });
        updated.set(resp.draft.id, resp.draft);
        latestGate = resp.gate_b;
        setTaskPlanStatus(resp.task_plan_status);
        setTaskPlanRevision(resp.task_plan_revision);
        expectedRevision = resp.task_plan_revision;
      }
      setDrafts((cur) => cur.map((draft) => updated.get(draft.id) ?? draft));
      if (latestGate) setGateB(latestGate);
    } catch (e) {
      setActionError(userFacingError(e));
    } finally {
      setApplyingRouteDraftId(null);
    }
  };

  const confirmTaskPlan = async () => {
    if (!sessionId) return;
    setConfirming(true);
    setActionError('');
    try {
      const resp = await v2Api.confirmTaskDrafts(sessionId);
      setGateB(resp.quality_gate);
      setTaskPlanStatus(resp.task_plan_status);
      setTaskPlanRevision(resp.task_plan_revision);
    } catch (e) {
      // Surface the human-readable error AND, when the backend
      // included ``detail.quality_gate`` (typical 409), refresh the
      // panel with the most recent gate-B result so the user sees
      // real outstanding issues instead of the stale snapshot.
      const msg = userFacingError(e);
      if (
        e instanceof Error &&
        typeof (e as unknown as { detail?: Record<string, unknown> }).detail === 'object'
      ) {
        const detail = (e as unknown as { detail: Record<string, unknown> }).detail;
        const qg = detail.quality_gate;
        if (qg && typeof qg === 'object') setGateB(qg as TaskPlanQualityGate);
      }
      setActionError(msg);
    } finally {
      setConfirming(false);
    }
  };

  const withdrawConfirm = async () => {
    if (!sessionId) return;
    setActionError('');
    try {
      const resp = await v2Api.withdrawTaskDraftsConfirm(sessionId);
      setDrafts(resp.drafts);
      setTaskPlanStatus(resp.task_plan_status);
      setTaskPlanRevision(resp.task_plan_revision);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <section className="v2-page" data-testid="v2-task-plan-page">
      <header className="v2-title"><div><span>当前步骤:任务拆解与执行计划确认</span><h1>{plan?.project_outline?.name ?? ''}</h1></div><strong>确认前不创建正式对象</strong></header>
      <section className="v2-panel">
        <div className="v2-callout">
          <strong>任务拆解与验收规划模型</strong>
          <span>{chosenTaskPlanModel}</span>

          <strong>真实执行代理</strong>
          <span data-testid="v2-task-plan-execution-agents">
            {selectedExecutionModel?.execution_adapter ? `${selectedExecutionModel.display_name || selectedExecutionModel.model} · ${selectedExecutionModel.execution_adapter}` : `${assignmentLabel(modelAssignment, 'executor') || '当前模型'} 执行代理未就绪`}
          </span>

          <strong>任务计划状态</strong>
          <span data-testid="v2-task-plan-status">{taskPlanStatus} · revision={taskPlanRevision}</span>

          <GateBPanel gateB={gateB} allDrafts={drafts} />

          <strong>执行路线来源</strong>
          <span className="muted-text" data-testid="v2-task-plan-auto-upgrade-state">
            任务内容由拆解模型生成；执行模型和执行代理固定使用系统设置或本页手动选择，不读取模型输出里的临时推荐。
          </span>
        </div>
      </section>

      {noDraftsYet ? (
        <section className="v2-panel">
          <h2>尚未生成任务草稿</h2>
          <p className="muted-text">点击下方按钮生成任务草稿。生成会覆盖已有草稿,完成后每条任务都会通过 Gate B 检查。</p>
          {actionError ? <p className="error-banner">{actionError}</p> : null}
          <button
            className="primary-button"
            data-testid="v2-task-plan-generate"
            disabled={generating || !selectedTaskPlanModel || !canGenerateFromBlueprint}
            onClick={() => void generate()}
          >{generating ? '生成中…' : '生成任务草稿'}</button>
        </section>
      ) : (
        <section className="v2-panel">
          <h2>任务草稿({drafts.length})</h2>
          {actionError ? <p className="error-banner">{actionError}</p> : null}
          <div className="v2-task-route" data-testid="v2-task-plan-bulk-route">
            <label><span>批量服务路线模型</span><select
              value={bulkSelectedModelValue || (bulkModelInvalid ? bulkRecommendedCodeModel : '')}
              disabled={!routeEditable}
              onChange={(e) => {
                const modelValue = e.target.value;
                setBulkRecommendedCodeModel(modelValue);
                const matched = executionModels.find((m) => executionModelValue(m) === modelValue);
                if (matched?.execution_adapter) setBulkExecutorAgent(matched.execution_adapter);
              }}
            >
              <option value="">请选择执行模型</option>
              {bulkModelInvalid ? (
                <option value={bulkRecommendedCodeModel}>模型配置无效：{bulkRecommendedCodeModel}</option>
              ) : null}
              {executionModels.map((m) => <option key={m.id} value={executionModelValue(m)}>{(m.display_name ?? m.id)} · {m.execution_adapter ?? ''}</option>)}
            </select></label>
            <label><span>批量执行代理</span><select
              value={bulkExecutorAgent}
              disabled={!routeEditable}
              onChange={(e) => setBulkExecutorAgent(e.target.value)}
            >
              {uniqueExecutionAdapters(executionModels).map((adapter) => (
                <option key={adapter} value={adapter}>{adapter}</option>
              ))}
              <option value="save_pending">save_pending (暂存待人工指派)</option>
            </select></label>
            <label><span>批量备份路由</span><select
              value={bulkBackupRoute}
              disabled={!routeEditable}
              onChange={(e) => setBulkBackupRoute(e.target.value)}
            >
              <option value="save_pending">save_pending</option>
              <option value="manual_route_switch">manual_route_switch</option>
            </select></label>
            <button
              type="button"
              className="primary-button"
              data-testid="v2-task-plan-apply-route-all"
              disabled={!routeEditable || !!applyingRouteDraftId || bulkModelInvalid || !bulkSelectedModelValue || !bulkExecutorAgent || !bulkBackupRoute}
              onClick={() => void applyRouteToAllDrafts()}
            >
              {applyingRouteDraftId === 'all' ? '应用中...' : '将此执行配置应用到全部任务'}
            </button>
            {!routeEditable ? (
              <span className="muted-text">任务计划已确认或正式项目已创建，执行配置已锁定。</span>
            ) : null}
          </div>
          {drafts.map((d) => {
            const taskSelectedModel = findExecutionModelByValue(d.recommended_code_model, executionModels);
            const taskSelectedModelValue = taskSelectedModel ? executionModelValue(taskSelectedModel) : '';
            const taskModelInvalid = !!d.recommended_code_model && !taskSelectedModelValue;
            return (
            <article className="v2-task-row" key={d.id} data-testid={`v2-task-row-${d.id}`}>
              <header>
                <input
                  className="v2-task-title-input"
                  value={d.title}
                  data-testid={`v2-task-title-${d.id}`}
                  onChange={(e) => setDrafts((cur) => cur.map((x) => x.id === d.id ? { ...x, title: e.target.value } : x))}
                  onBlur={(e) => e.target.value !== d.title && void patchDraft(d.id, { title: e.target.value })}
                />
                <span className="muted-text" data-testid={`v2-task-status-${d.id}`}>{d.status}</span>
              </header>
              <dl className="v2-task-fields" data-testid={`v2-task-fields-${d.id}`}>
                <div><dt>优先级</dt><dd>{d.priority ?? '(未填)'}</dd></div>
                <div><dt>难度</dt><dd>{d.difficulty ?? '(未填)'}</dd></div>
                <div><dt>工作量</dt><dd>{d.effort ?? '(未填)'}</dd></div>
                <div><dt>风险</dt><dd>{d.risk_level ?? '(未填)'}</dd></div>
                <div><dt>服务路线模型</dt><dd>{d.recommended_code_model ?? '(未选 — 草稿将标 save_pending)'}</dd></div>
                <div><dt>执行代理</dt><dd>{d.executor_agent ?? '(未指定)'}</dd></div>
                <div><dt>备份路由</dt><dd>{d.backup_route ?? '(未指定)'}</dd></div>
                <div><dt>来源阶段</dt><dd>{d.source_stage_id || d.blueprint_phase_id}</dd></div>
              </dl>
              <label><span>任务目标</span><textarea rows={2} value={d.objective} onChange={(e) => setDrafts((cur) => cur.map((x) => x.id === d.id ? { ...x, objective: e.target.value } : x))} onBlur={(e) => e.target.value !== d.objective && void patchDraft(d.id, { objective: e.target.value })} /></label>
              <label><span>预期交付物</span><textarea rows={2} value={d.expected_deliverable} onChange={(e) => setDrafts((cur) => cur.map((x) => x.id === d.id ? { ...x, expected_deliverable: e.target.value } : x))} onBlur={(e) => e.target.value !== d.expected_deliverable && void patchDraft(d.id, { expected_deliverable: e.target.value })} /></label>
              <label><span>验收标准(逐行)</span><textarea rows={Math.max(2, d.task_acceptance.length + 1)} value={d.task_acceptance.join('\n')} onChange={(e) => setDrafts((cur) => cur.map((x) => x.id === d.id ? { ...x, task_acceptance: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) } : x))} onBlur={(e) => {
                const next = e.target.value.split('\n').map((s) => s.trim()).filter(Boolean);
                if (JSON.stringify(next) !== JSON.stringify(d.task_acceptance)) void patchDraft(d.id, { task_acceptance: next });
              }} /></label>
              <label><span>任务依赖 draft id(逐行)</span><textarea rows={Math.max(2, (d.dependency_task_draft_ids || []).length + 1)} value={(d.dependency_task_draft_ids || []).join('\n')} onChange={(e) => setDrafts((cur) => cur.map((x) => x.id === d.id ? { ...x, dependency_task_draft_ids: e.target.value.split(/[\n,，]/).map((s) => s.trim()).filter(Boolean) } : x))} onBlur={(e) => {
                const next = e.target.value.split(/[\n,，]/).map((s) => s.trim()).filter(Boolean);
                if (JSON.stringify(next) !== JSON.stringify(d.dependency_task_draft_ids || [])) void patchDraft(d.id, { dependency_task_draft_ids: next });
              }} /></label>
              <button
                type="button"
                className="ghost-button"
                data-testid={`v2-task-save-${d.id}`}
                disabled={!routeEditable || busy}
                onClick={() => void patchDraft(d.id, {
                  title: d.title,
                  objective: d.objective,
                  expected_deliverable: d.expected_deliverable,
                  task_acceptance: d.task_acceptance,
                  dependency_task_draft_ids: d.dependency_task_draft_ids || [],
                })}
              >保存任务修改</button>
              <div className="v2-task-route">
                <label><span>执行路线模型</span><select
                  value={taskSelectedModelValue || (taskModelInvalid ? d.recommended_code_model || '' : '')}
                  data-testid={`v2-task-model-${d.id}`}
                  disabled={!routeEditable}
                  onChange={(e) => {
                    const next = e.target.value || null;
                    const matched = findExecutionModelByValue(next, executionModels);
                    const nextExecutor = matched?.execution_adapter || d.executor_agent;
                    setDrafts((cur) => cur.map((x) => x.id === d.id ? { ...x, recommended_code_model: next, executor_agent: nextExecutor } : x));
                    // Persist immediately on the select event — <select>
                    // does not auto-blur after a value change, so an
                    // onBlur handler would never fire and the backend
                    // would stay stale, leaving Gate B panel showing
                    // the pre-edit snapshot.
                    if (next !== d.recommended_code_model) {
                      void patchDraft(d.id, { recommended_code_model: next, executor_agent: nextExecutor });
                    }
                  }}
                >
                  <option value="">(未选择 — 草稿将标 save_pending)</option>
                  {taskModelInvalid ? (
                    <option value={d.recommended_code_model || ''}>模型配置无效：{d.recommended_code_model}</option>
                  ) : null}
                  {executionModels.map((m) => <option key={m.id} value={executionModelValue(m)}>{(m.display_name ?? m.id)} · {m.execution_adapter ?? ''}</option>)}
                </select></label>
                <label><span>executor_agent</span><select
                  value={d.executor_agent}
                  data-testid={`v2-task-executor-${d.id}`}
                  disabled={!routeEditable}
                  onChange={(e) => {
                    const next = e.target.value;
                    setDrafts((cur) => cur.map((x) => x.id === d.id ? { ...x, executor_agent: next } : x));
                    if (next !== d.executor_agent) {
                      void patchDraft(d.id, { executor_agent: next });
                    }
                  }}
                >
                  {uniqueExecutionAdapters(executionModels).map((adapter) => (
                    <option key={adapter} value={adapter}>{adapter}</option>
                  ))}
                  <option value="save_pending">save_pending (暂存待人工指派)</option>
                </select></label>
                <label><span>backup_route</span><select
                  value={d.backup_route}
                  data-testid={`v2-task-backup-${d.id}`}
                  disabled={!routeEditable}
                  onChange={(e) => {
                    const next = e.target.value;
                    setDrafts((cur) => cur.map((x) => x.id === d.id ? { ...x, backup_route: next } : x));
                    if (next !== d.backup_route) {
                      void patchDraft(d.id, { backup_route: next });
                    }
                  }}
                >
                  <option value="save_pending">save_pending</option>
                  <option value="manual_route_switch">manual_route_switch</option>
                </select></label>
              </div>
            </article>
            );
          })}
        </section>
      )}

      <footer className="v2-sticky-actions">
        <Link className="ghost-button" to={`/draft/${sessionId}/outline`}>返回项目大纲</Link>
        {!noDraftsYet ? (
          <>
            <button className="ghost-button" data-testid="v2-task-plan-regenerate" disabled={generating || !selectedTaskPlanModel} onClick={() => void generate()}>{generating ? '重新生成中…' : '重新生成任务草稿'}</button>
            <button
              className={gateBReady ? 'primary-button' : 'ghost-button'}
              data-testid="v2-task-plan-confirm"
              disabled={confirming || busy || !coverageReady || (!gateBReady && !drafts.length) || (gateBReady && !canConfirm)}
              onClick={() => gateBReady && coverageReady ? void confirmTaskPlan() : void repairTaskDrafts()}
            >{gateBReady && coverageReady ? '确认任务计划' : busy ? '正在修正任务草稿…' : '自动修正任务草稿'}</button>
            {taskPlanStatus === 'user_confirmed' && !createdProjectId ? (
              <button className="ghost-button" data-testid="v2-task-plan-withdraw" disabled={busy} onClick={() => void withdrawConfirm()}>撤回任务计划确认(返回编辑)</button>
            ) : null}
          </>
        ) : null}
        <button
          className="primary-button"
          data-testid="v2-task-plan-create-project"
          disabled={taskPlanStatus !== 'user_confirmed' || busy}
          onClick={async () => {
            if (!sessionId) return;
            if (createdProjectId) {
              navigate(`/project/${createdProjectId}`);
              return;
            }
            setBusy(true);
            setActionError('');
            try {
              const result: any = await v2Api.confirmExecutionPlan(sessionId);
              if (result?.created_project_id) navigate(`/project/${result.created_project_id}`);
            } catch (e) {
              setActionError(e instanceof Error ? e.message : String(e));
            } finally {
              setBusy(false);
            }
          }}
        >{createdProjectId ? '进入正式项目' : taskPlanStatus === 'user_confirmed' ? '确认执行计划,创建正式项目' : '需先确认任务计划'}</button>
      </footer>
    </section>
  );
}

export function ProjectsV2() {
  const [view, setView] = useState('active');
  const [projects, setProjects] = useState<Project[]>([]);
  const [bin, setBin] = useState<RecycleBinEntry[]>([]);
  const [message, setMessage] = useState('');
  const load = useCallback(() => {
    if (view === 'recycle-bin') void v2Api.recycleBin().then(setBin).catch(() => setBin([]));
    else void v2Api.listProjects(view).then(setProjects).catch(() => setProjects([]));
  }, [view]);
  useEffect(() => { load(); }, [load]);
  const act = async (label: string, fn: () => Promise<unknown>) => {
    setMessage('');
    try {
      await fn();
      setMessage(`${label}完成`);
      await load();
    } catch (e) {
      setMessage(`${label}失败：${e instanceof Error ? e.message : String(e)}`);
    }
  };
  return <section className="v2-page"><header className="v2-title"><h1>正式项目库</h1><Link className="primary-button" to="/">新建项目</Link></header>
    <nav className="v2-tabs">{['active', 'archived', 'recycle-bin', 'all'].map((key) => <button className={view === key ? 'active' : ''} key={key} onClick={() => setView(key)}>{key === 'active' ? '进行中' : key === 'archived' ? '归档' : key === 'recycle-bin' ? '回收站' : '全部'}</button>)}</nav>
    {message ? <p className={message.includes('失败') ? 'error-banner' : 'success-banner'}>{message}</p> : null}
    {view === 'recycle-bin' ? <div className="v2-card-list">{bin.map((item) => <article className="v2-card" key={item.project_id}><h2>{item.name}</h2><p>剩余 {item.days_remaining} 天 · {fmt(item.deleted_at)}</p><footer><button className="ghost-button" onClick={() => void act('恢复项目', () => v2Api.restoreProject(item.project_id))}>恢复</button></footer></article>)}</div>
      : <div className="v2-card-list">{projects.map((project) => <article className="v2-card" key={project.id}><h2>{project.name}</h2><p>{project.slug}</p><footer><Link className="primary-button" to={`/project/${project.id}`}>进入驾驶舱</Link>{view === 'archived' ? <button className="ghost-button" onClick={() => void act('取消归档', () => v2Api.unarchiveProject(project.id))}>取消归档</button> : <button className="ghost-button" onClick={() => void act('归档', () => v2Api.archiveProject(project.id))}>归档</button>}<button className="danger-button" onClick={() => { if (confirm(`删除项目“${project.name}”？项目会进入回收站。`)) void act('删除项目', () => v2Api.deleteProject(project.id)); }}>删除</button></footer></article>)}</div>}
  </section>;
}

export function ProjectDashboardV2() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<ProjectDashboard | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [lastStartedTaskId, setLastStartedTaskId] = useState<string>('');
  const load = useCallback(async () => {
    if (!projectId) return;
    const next = await v2Api.dashboard(projectId);
    setData(next);
  }, [projectId]);
  useEffect(() => { void load(); }, [load]);
  const startFirstTask = async () => {
    if (!data?.first_task_package?.task_package_id) return;
    setBusy(true);
    setActionError('');
    try {
      const r = await v2Api.startTask(data.session_id || data.project_id, data.first_task_package.task_package_id);
      const tid = String((r as any)?.task_id || (r as any)?.created_task_id || (r as any)?.existing_task_id || (r as any)?.id || '');
      setLastStartedTaskId(tid);
      if (tid) {
        navigate(`/task/${tid}`);
        return;
      }
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const finalAcceptProject = async () => {
    if (!projectId) return;
    setBusy(true);
    setActionError('');
    try {
      await v2Api.finalAcceptProject(projectId, { actor: 'v2_project_dashboard', note: 'V2 驾驶舱项目总验收通过' });
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
      await load().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };
  const createRepairPlan = async () => {
    if (!projectId) return;
    setBusy(true);
    setActionError('');
    try {
      await v2Api.createRepairPlan(projectId);
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
      await load().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };
  const confirmRepairPlan = async () => {
    if (!projectId) return;
    setBusy(true);
    setActionError('');
    try {
      await v2Api.confirmRepairPlan(projectId, { actor: 'v2_project_dashboard', note: '用户确认返修计划' });
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
      await load().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };
  const firstPkg = data?.first_task_package;
  const deliveryArtifacts = data?.delivery_artifacts ?? [];
  const deliveryCenter = data?.delivery_center;
  const primaryArtifact = deliveryCenter?.primary_artifact || null;
  const historicalArtifacts = deliveryArtifacts.filter((artifact: any) => artifact.task_id !== primaryArtifact?.task_id).reverse();
  const terminalProject = data?.project_user_view?.project_status === 'accepted_delivered';
  const currentStageLabel = terminalProject ? '全部阶段已完成' : data?.current_stage?.name || '等待生成阶段';
  const currentTaskLabel = terminalProject ? '无当前任务' : data?.current_task?.title || (firstPkg ? `${firstPkg.stage_name} · ${firstPkg.title}` : '无当前任务');
  const finalProductReady = !!deliveryCenter?.requirement_completion?.passed;
  const productQualityGate = data?.final_acceptance?.product_quality_gate;
  const productQualityPassed = !productQualityGate || productQualityGate.passed !== false;
  const showStartButton = !!data
    && ['开始执行第一个任务', '开始执行下一个任务', '开始执行返修任务'].includes(data.next_action)
    && !!firstPkg
    && !firstPkg.task_id;
  const showFinalAcceptButton = !!data
    && data.status === 'awaiting_project_acceptance'
    && data.accepted_delivery_progress.accepted_stages === data.accepted_delivery_progress.total_stages
    && data.accepted_delivery_progress.total_stages > 0
    && productQualityPassed;
  const showRepairPlanButton = !!data && data.status === 'project_repair_required'
    && (!data.repair_plan || ['failed'].includes(String(data.repair_plan.status || '').toLowerCase()));
  const showConfirmRepairPlanButton = !!data && data.status === 'project_repair_required' && data.repair_plan?.status === 'proposed';
  useEffect(() => {
    if (!data || busy || actionError) return;
    const currentTask = data.current_task;
    const shouldPoll = !!currentTask?.task_id && currentTask.package_status !== 'accepted';
    if (!shouldPoll) return;
    const timer = window.setTimeout(() => { void load(); }, 5000);
    return () => window.clearTimeout(timer);
  }, [data, busy, actionError, load]);
  if (!data) return <div className="empty-state">读取项目驾驶舱...</div>;
  const outlineReviewHref = data.session_id ? `/draft/${data.session_id}/outline` : '';
  const taskPlanReviewHref = data.session_id ? `/draft/${data.session_id}/task-plan` : '';
  return <section className="v2-page" data-testid="v2-project-dashboard"><header className="v2-title"><div><span>正式项目驾驶舱</span><h1>{data.project_name}</h1></div><strong>{data.user_status || data.status}</strong></header>
    <div className="v2-progress-grid" data-testid="v2-dashboard-progress">
      <article><strong>规划完成度</strong><p>{Object.entries(data.planning_progress).filter(([, v]) => v).length} / {Object.keys(data.planning_progress).length}</p></article>
      <article><strong>工程执行完成度</strong><p>{data.engineering_progress.percent}% · {data.engineering_progress.completed_tasks}/{data.engineering_progress.total_tasks} 任务</p></article>
      <article><strong>已验收交付完成度</strong><p>{data.accepted_delivery_progress.percent}% · {data.accepted_delivery_progress.accepted_stages}/{data.accepted_delivery_progress.total_stages} 阶段</p></article>
      <article><strong>当前卡点</strong><p>{data.current_blocker}</p></article>
    </div>
    <section className="v2-panel" data-testid="v2-dashboard-planning-review">
      <h2>规划回看</h2>
      {data.session_id ? (
        <div className="button-row">
          <Link className="ghost-button" data-testid="v2-dashboard-outline-review" to={outlineReviewHref}>回看项目大纲</Link>
          <Link className="ghost-button" data-testid="v2-dashboard-task-plan-review" to={taskPlanReviewHref}>回看任务拆解</Link>
        </div>
      ) : (
        <p className="muted-text">此项目缺少草稿会话关联，暂无法回看大纲和任务拆解快照。</p>
      )}
    </section>
    <section className="v2-panel" data-testid="v2-dashboard-next-action">
      <h2>当前阶段与当前任务</h2>
      <p>{currentStageLabel} / {currentTaskLabel}</p>
      <p>等待：{data.waiting_for}</p>
      {firstPkg ? (
        <p className="muted-text" data-testid="v2-dashboard-first-pkg">
          下一个待执行任务包:{firstPkg.stage_name} · {firstPkg.title}
          {firstPkg.task_id ? ' (已建立 Task,正在执行)' : ' (尚未启动)'}
        </p>
      ) : null}
      {showRepairPlanButton ? (
        <button className="primary-button" data-testid="v2-dashboard-create-repair-plan" disabled={busy} onClick={() => void createRepairPlan()}>
          {busy ? '生成返修计划中…' : '生成返修计划'}
        </button>
      ) : showConfirmRepairPlanButton ? (
        <button className="primary-button" data-testid="v2-dashboard-confirm-repair-plan" disabled={busy} onClick={() => void confirmRepairPlan()}>
          {busy ? '确认返修计划中…' : '确认返修计划并执行'}
        </button>
      ) : showStartButton ? (
        <button
          className="primary-button"
          data-testid="v2-dashboard-start-first-task"
          disabled={busy}
          onClick={() => void startFirstTask()}
        >{busy ? '启动中…' : data.next_action}</button>
      ) : showFinalAcceptButton ? (
        <button
          className="primary-button"
          data-testid="v2-dashboard-final-accept-project"
          disabled={busy}
          onClick={() => void finalAcceptProject()}
        >{busy ? '处理中…' : '通过项目总验收'}</button>
      ) : terminalProject ? (
        <Link className="ghost-button" data-testid="v2-dashboard-delivery-link" to={`/v3/delivery/${data.project_id}`}>查看交付物</Link>
      ) : (
        <Link className="ghost-button" data-testid="v2-dashboard-stage-link" to={`/project/${data.project_id}/stage/${data.current_stage?.id ?? data.stages[0]?.id ?? ''}`}>{data.next_action}</Link>
      )}
      {actionError ? <p className="error-banner" data-testid="v2-dashboard-action-error">{actionError}</p> : null}
      {productQualityGate ? (
        <div className={productQualityGate.passed ? 'success-banner' : 'error-banner'} data-testid="v2-dashboard-product-quality-gate">
          <strong>最终成品质量门：{productQualityGate.passed ? '已通过' : '未通过'}</strong>
          {productQualityGate.passed ? (
            <p>{terminalProject ? '项目已完成；质量门结果保留在历史审计中。' : '最终成品入口已定位，项目总验收可继续。'}</p>
          ) : (
            <ul>
              {(productQualityGate.issues || []).map((issue: string) => <li key={issue}>{issue}</li>)}
            </ul>
          )}
        </div>
      ) : null}
      {data.repair_plan ? (
        <div className="v2-card" data-testid="v2-dashboard-repair-plan">
          <h3>返修计划 · R{String(data.repair_plan.revision || '-')} · {data.repair_plan.status}</h3>
          <ul>{(data.repair_plan.items || []).map((item: any) => <li key={item.item_id}>{item.issue} · {item.status}</li>)}</ul>
        </div>
      ) : null}
      {lastStartedTaskId ? <p className="muted-text">已创建 Task:<code>{lastStartedTaskId}</code></p> : null}
    </section>
    <section className="v2-panel" data-testid="v2-dashboard-delivery-center">
      <h2>交付中心</h2>
      <div className="v2-kv-grid">
        <strong>产品类型</strong><span>{deliveryCenter?.product_type || '待识别'}</span>
        <strong>正式版本号</strong><span>{deliveryCenter?.version || '正式版本号未记录'}</span>
        <strong>项目交付 revision</strong><span>{deliveryCenter?.project_revision || '未记录'}</span>
        <strong>系统应用版本</strong><span>{deliveryCenter?.application_version || '未记录'}</span>
        <strong>验收结果</strong><span>{deliveryCenter?.requirement_completion?.passed ? '通过' : '未通过或未完成'}</span>
      </div>
      {primaryArtifact ? (
        <article className="v2-card" data-testid="v2-dashboard-primary-artifact">
          <h3>{finalProductReady ? '最终成品预览' : '当前任务成品预览'}：{primaryArtifact.title}</h3>
          <p className="muted-text">{primaryArtifact.stage_name}</p>
          <footer>
            {deliveryCenter?.final_preview ? <a className="primary-button" href={deliveryCenter.final_preview}>{finalProductReady ? '打开最终成品预览' : '打开成品预览'}</a> : <button className="ghost-button" disabled>暂无可验证预览</button>}
            {deliveryCenter?.download_url ? <a className="ghost-button" href={deliveryCenter.download_url}>下载交付包</a> : <button className="ghost-button" disabled>暂无可验证交付包</button>}
            <Link className="ghost-button" to={`/task/${primaryArtifact.task_id}`}>查看任务交付</Link>
            {!terminalProject ? <Link className="danger-button" to={`/task/${primaryArtifact.task_id}`}>要求调整</Link> : null}
          </footer>
        </article>
      ) : (
        <p className="muted-text">当前还没有可访问的成品入口；执行任务后应先在人工验收页看到预览和交付文件。</p>
      )}
      <div className="v2-card-list">
        <article className="v2-card">
          <h3>原始需求完成情况</h3>
          <p>{deliveryCenter?.requirement_completion?.passed ? '核心需求已在最终产物中闭环。' : '仍有核心需求未在最终产物中体现。'}</p>
          {(deliveryCenter?.requirement_completion?.missing_core_goals || []).length ? (
            <ul>{deliveryCenter.requirement_completion.missing_core_goals.map((item: string) => <li key={item}>{item}</li>)}</ul>
          ) : null}
        </article>
        <article className="v2-card">
          <h3>未完成项与已知问题</h3>
          {(deliveryCenter?.unresolved_items || []).length ? (
            <ul>{deliveryCenter.unresolved_items.map((item: string) => <li key={item}>{item}</li>)}</ul>
          ) : <p>暂无未完成项。</p>}
        </article>
        <article className="v2-card">
          <h3>使用说明</h3>
          <p>{deliveryCenter?.usage_instructions || '交付完成后显示使用说明。'}</p>
          <h3>测试说明</h3>
          <p>{deliveryCenter?.test_instructions || '交付完成后显示测试说明。'}</p>
        </article>
      </div>
      {(deliveryCenter?.delivery_files || []).length ? (
        <details className="v2-mini-block" open data-testid="v2-dashboard-delivery-files">
          <summary>全部交付文件({deliveryCenter.delivery_files.length})</summary>
          <div className="v2-card-list">
            {deliveryCenter.delivery_files.map((artifact: any) => (
              <article className="v2-card" key={`${artifact.stage_name}-${artifact.title}`}>
                <h3>{artifact.title}</h3>
                <p className="muted-text">{artifact.stage_name}</p>
                <footer>
                  {artifact.preview_url ? <a className="ghost-button" href={artifact.preview_url}>打开预览</a> : <button className="ghost-button" disabled>暂无可验证预览</button>}
                  {artifact.download_url ? <a className="ghost-button" href={artifact.download_url}>下载文件</a> : <button className="ghost-button" disabled>暂无可验证交付包</button>}
                </footer>
              </article>
            ))}
          </div>
        </details>
      ) : null}
      {historicalArtifacts.length ? (
        <details className="v2-mini-block" data-testid="v2-dashboard-artifact-history">
          <summary>过程交付/历史预览({historicalArtifacts.length})</summary>
          <div className="v2-card-list">
            {historicalArtifacts.map((artifact: any) => (
              <article className="v2-card" key={artifact.task_id}>
                <h3>{artifact.title}</h3>
                <p className="muted-text">{artifact.stage_name}</p>
                <footer>
                  <a className="ghost-button" href={artifact.preview_url}>打开过程预览</a>
                  <Link className="ghost-button" to={`/task/${artifact.task_id}`}>查看任务交付</Link>
                </footer>
              </article>
            ))}
          </div>
        </details>
      ) : null}
    </section>
    <section className="v2-panel"><h2>阶段轨迹</h2><div className="v2-timeline">{data.session_id ? <Link className="done" to={outlineReviewHref}>项目大纲</Link> : <span className="done">项目大纲</span>}{data.session_id ? <Link className="done" to={taskPlanReviewHref}>任务拆解</Link> : <span className="done">任务拆解</span>}{data.stages.map((s) => <Link key={s.id} className={s.progress.can_enter_next_stage ? 'done' : 'current'} to={`/project/${data.project_id}/stage/${s.id}`}>{s.name}执行与验收</Link>)}<span>项目总验收</span><span>已交付</span></div></section>
  </section>;
}

export function StageV2() {
  const { projectId, stageId } = useParams();
  const navigate = useNavigate();
  const [stage, setStage] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const loadStage = useCallback(async () => {
    if (projectId && stageId) setStage(await v2Api.stage(projectId, stageId));
  }, [projectId, stageId]);
  useEffect(() => { void loadStage(); }, [loadStage]);
  const acceptStage = async () => {
    if (!stageId) return;
    setBusy(true);
    setError('');
    try {
      await v2Api.acceptStage(stageId, { actor: 'v2_stage_page', note: 'V2 阶段页手动兜底确认通过' });
      await loadStage();
      if (projectId) navigate(`/project/${projectId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      await loadStage().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };
  const canAcceptStage = !!stage?.progress?.can_accept_stage;
  const stageTasks = Array.isArray(stage?.tasks) ? stage.tasks : [];
  const pendingTasks = Array.isArray(stage?.acceptance_blockers)
    ? stage.acceptance_blockers
    : stageTasks.filter((task: any) => task.package_status !== 'accepted');
  const pendingSummary = pendingTasks
    .map((task: any) => `${task.title}(${task.state || task.task_status || task.package_status || '未记录'})`)
    .join('；');
  const needsPolling = stageTasks.some((task: any) => task.package_status !== 'accepted');
  const stageBlockReason = !stage ? ''
    : !hasAcceptance(stage.acceptance) ? '阶段缺少验收标准，不能阶段验收。'
      : pendingTasks.length > 0 ? `还有 ${pendingTasks.length} 个任务包未验收：${pendingSummary}`
        : canAcceptStage ? '阶段任务已全部通过人工验收，可以由用户明确点击阶段验收。'
          : '阶段状态已处理或当前状态不允许重复验收。';
  useEffect(() => {
    if (!stage || busy || error || !needsPolling) return;
    const timer = window.setTimeout(() => { void loadStage(); }, 5000);
    return () => window.clearTimeout(timer);
  }, [stage, busy, error, needsPolling, loadStage]);
  if (!stage) return <div className="empty-state">读取阶段...</div>;
  return <section className="v2-page" data-testid="v2-stage-page"><header className="v2-title"><div><span>阶段执行与阶段验收</span><h1>{stage.name}</h1></div><strong>{stage.progress.stage_acceptance_status}</strong></header>
    <section className="v2-panel"><h2>阶段验收标准</h2><EditableList items={stage.acceptance} /><p>通过阶段验收后将解锁：{stage.unlock_target}</p><p className={canAcceptStage ? 'success-banner' : 'muted-text'} data-testid="v2-stage-block-reason">{stageBlockReason}</p></section>
    <section className="v2-panel"><h2>阶段全部任务</h2>{stageTasks.map((task: any) => <article className="v2-card" key={task.task_package_id}><h3>{task.title}</h3><p>{task.state}</p><p className="muted-text">任务包状态：{task.package_status || task.status || '未记录'}</p><EditableList items={task.acceptance} />{task.task_id ? <Link className="ghost-button" to={`/task/${task.task_id}`}>查看任务交付</Link> : null}</article>)}</section>
    {error ? <p className="error-banner">{error}</p> : null}
    <footer className="v2-sticky-actions"><button className="primary-button" disabled={busy || !canAcceptStage} onClick={() => void acceptStage()}>{busy ? '处理中...' : '通过阶段验收'}</button><button className="danger-button" disabled title="请在任务交付页提交具体修复意见">阶段问题请到任务页返修</button><button className="ghost-button" disabled={busy} onClick={() => projectId ? navigate(`/project/${projectId}`) : undefined}>暂不处理</button></footer>
  </section>;
}

export function TaskV2() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [repairOpen, setRepairOpen] = useState(false);
  const [repairReason, setRepairReason] = useState('');
  const [repairSubmitted, setRepairSubmitted] = useState('');
  const loadTask = useCallback(async () => {
    if (!taskId) return;
    setTask(await v2Api.task(taskId));
  }, [taskId]);
  useEffect(() => { void loadTask(); }, [loadTask]);
  const status = String(task?.status || '').toLowerCase();
  const running = ['queued', 'pending', 'planning', 'running'].includes(status);
  const failed = status === 'failed';
  const awaitingReview = status === 'awaiting_review';
  const canApprove = awaitingReview;
  const canRedispatch = Boolean(task?.can_redispatch);
  useEffect(() => {
    if (!task || busy || error) return;
    if (!running && !awaitingReview) return;
    const timer = window.setTimeout(() => { void loadTask(); }, 5000);
    return () => window.clearTimeout(timer);
  }, [task, busy, error, running, awaitingReview, loadTask]);
  const retry = async () => {
    if (!taskId) return;
    setBusy(true);
    setError('');
    try {
      await v2Api.retryTask(taskId);
      await loadTask();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      await loadTask().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };
  const redispatch = async () => {
    if (!taskId || !canRedispatch) return;
    setBusy(true);
    setError('');
    try {
      await v2Api.redispatchTask(taskId);
      await loadTask();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      await loadTask().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };
  async function review(decision: 'approve' | 'reject' | 'require_fix') {
    if (!taskId) return;
    const reason = repairReason.trim();
    if (decision !== 'approve' && !reason) {
      setRepairOpen(true);
      setError('请先填写具体修复意见。');
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (decision === 'approve') {
        await v2Api.reviewTask(taskId, 'approve', reason || '用户查看交付后通过任务验收');
      } else {
        await v2Api.reviewTask(taskId, decision, reason);
        if (decision === 'require_fix') {
          setRepairSubmitted(reason);
          setRepairReason('');
          setRepairOpen(false);
        }
      }
      await loadTask();
      if (decision === 'approve' && task?.project_id) navigate(`/project/${task.project_id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      await loadTask().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  }
  if (!task) return <div className="empty-state">读取任务...</div>;
  const stateText = failed ? '执行失败' : running ? (task.state || '执行中') : (task.state || status);
  const changedFiles = Array.isArray(task.changed_files) ? task.changed_files : [];
  const steps = Array.isArray(task.execution_steps) ? task.execution_steps : [];
  const testResult = task.test_result && Object.keys(task.test_result).length ? task.test_result : null;
  return <section className="v2-page" data-testid="v2-task-page"><header className="v2-title"><div><span>单任务交付与任务验收</span><h1>{task.title}</h1></div><strong>{stateText}</strong></header>
    <section className="v2-panel">
      <div className="v2-callout">
        <strong>真实执行状态</strong><span>{stateText}</span>
        <strong>执行模型</strong><span>{task.code_model || '未记录'}</span>
        <strong>执行代理</strong><span>{task.executor_agent || '未记录'}</span>
        <strong>最近 Run</strong><span>{task.latest_run_status ? `#${task.latest_run_number || '-'} · ${task.latest_run_status}` : '暂无 Run'}</span>
        <strong>任务包状态</strong><span>{task.task_package_status || '未记录'}</span>
        {task.repair_revision ? <><strong>返修 revision</strong><span>R{String(task.repair_revision)}</span></> : null}
        {task.repair_reason ? <><strong>原返修意见</strong><span>{task.repair_reason}</span></> : null}
      </div>
      {failed ? <p className="error-banner">{task.execution_result || '执行失败，请查看日志后重试。'}</p> : null}
      {running ? <p className="muted-text">任务已进入执行链路，请稍后刷新查看结果。</p> : null}
      {error ? <p className="error-banner">{error}</p> : null}
    </section>
    <section className="v2-panel">
      <h2>任务交付</h2>
      <p>{task.delivery_summary || task.execution_result}</p>
      <EditableList items={task.deliverables} />
      {changedFiles.length ? <div className="v2-mini-block"><strong>已修改文件</strong><ul>{changedFiles.map((file: string) => <li key={file}><code>{file}</code></li>)}</ul></div> : null}
      {testResult ? <div className="v2-mini-block"><strong>构建/测试</strong><p><code>{testResult.command || '未记录命令'}</code> · exit {String(testResult.exit_code ?? '-')}</p>{testResult.stderr ? <pre>{testResult.stderr}</pre> : null}{testResult.stdout ? <pre>{testResult.stdout}</pre> : null}</div> : null}
    </section>
    {steps.length ? <section className="v2-panel">
      <h2>执行步骤</h2>
      <div className="v2-step-list">
        {steps.map((step: any) => <article className="v2-card" key={`${step.number}-${step.kind}`}>
          <h3>#{step.number} {step.kind} · {step.success === false ? '失败' : '成功'}</h3>
          {step.model_used ? <p>模型/工具：{step.model_used}</p> : null}
          {step.summary ? <p>{step.summary}</p> : null}
          {step.command ? <p><code>{step.command}</code> · exit {String(step.exit_code ?? '-')}</p> : null}
          {Array.isArray(step.changed_files) && step.changed_files.length ? <p>变更：{step.changed_files.join('、')}</p> : null}
          {step.error ? <p className="error-banner">{step.error}</p> : null}
        </article>)}
      </div>
    </section> : null}
    {Array.isArray(task.run_history) && task.run_history.length > 1 ? <section className="v2-panel">
      <h2>Run 历史</h2>
      <div className="v2-step-list">
        {task.run_history.map((run: any) => <article className="v2-card" key={run.id}>
          <h3>Run #{run.run_number} · {run.status}</h3>
          <p>{run.runner_type || 'worker'} · {run.triggered_by || 'unknown'}{run.parent_run_id ? ` · parent ${run.parent_run_id}` : ''}</p>
          {run.error_message ? <p className="error-banner">{run.error_message}</p> : null}
        </article>)}
      </div>
    </section> : null}
    <section className="v2-panel"><h2>任务验收标准</h2><EditableList items={task.acceptance} /></section>
    {repairOpen ? <section className="v2-panel" data-testid="v2-task-repair-panel">
      <h2>修复意见</h2>
      <textarea
        rows={5}
        value={repairReason}
        onChange={(e) => setRepairReason(e.target.value)}
        placeholder="写清楚哪里不满意、希望如何调整、必须保留哪些内容"
        data-testid="v2-task-repair-reason"
      />
      <div className="v2-action-row">
        <button className="danger-button" disabled={busy || !repairReason.trim()} onClick={() => void review('require_fix')}>提交修复意见</button>
        <button className="danger-button" disabled={busy || !repairReason.trim()} onClick={() => void review('reject')}>提交驳回</button>
        <button className="ghost-button" disabled={busy} onClick={() => setRepairOpen(false)}>取消</button>
      </div>
    </section> : null}
    {repairSubmitted ? <p className="success-banner" data-testid="v2-task-repair-submitted">已提交修复意见：{repairSubmitted}</p> : null}
    <footer className="v2-sticky-actions">
      {failed ? <button className="primary-button" disabled={busy || !task.can_retry} onClick={() => void retry()}>{busy ? '重新执行中...' : task.can_retry ? '重新执行任务' : '已达到重试上限'}</button> : null}
      {status === 'queued' && canRedispatch ? <button className="primary-button" disabled={busy} onClick={() => void redispatch()}>{busy ? '重新派发中...' : '重新派发当前 Run'}</button> : null}
      <button className="primary-button" disabled={busy || !canApprove} onClick={() => void review('approve')}>{busy ? '处理中...' : '通过任务验收'}</button>
      <button className="danger-button" disabled={busy || !awaitingReview} onClick={() => { setError(''); setRepairOpen(true); }}>要求修复</button>
      <button className="danger-button" disabled={busy || !awaitingReview} onClick={() => { setError(''); setRepairOpen(true); }}>驳回任务</button>
      <button className="ghost-button" disabled={busy} onClick={() => void loadTask()}>刷新状态</button>
    </footer>
  </section>;
}

export function LegacyRedirect({ kind }: { kind: 'workbench' | 'draft' | 'outline' | 'taskPlan' | 'tasks' | 'task' | 'fallback' }) {
  const params = useParams();
  const [search] = useSearchParams();
  if (kind === 'workbench') {
    const projectId = search.get('project_id');
    return projectId ? <Navigate replace to={`/project/${projectId}`} /> : <Navigate replace to="/legacy" />;
  }
  if (kind === 'draft') return <Navigate replace to={`/draft/${params.sessionId}/clarify`} />;
  if (kind === 'outline') return <Navigate replace to={`/draft/${params.sessionId}/outline`} />;
  if (kind === 'taskPlan') return <Navigate replace to={`/draft/${params.sessionId}/task-plan`} />;
  if (kind === 'task') return <Navigate replace to={`/task/${params.taskId}`} />;
  if (kind === 'tasks') {
    const projectId = search.get('project_id');
    return projectId ? <Navigate replace to={`/project/${projectId}`} /> : <Navigate replace to="/projects" />;
  }
  return <Navigate replace to="/legacy" />;
}
